//Dependencies
import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
  useLayoutEffect,
  useEffect,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useFilterKeyboardShortcuts } from '../../../../hooks/useFilterKeyboardShortcuts';
import { useTableCurrentPage } from '../../../../hooks/useTableCurrentPage';
import {
  createColumnHelper,
  ColumnFiltersState,
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  flexRender,
  getPaginationRowModel,
  PaginationState,
  VisibilityState,
  FilterFnOption,
  Row,
} from '@tanstack/react-table';
import { format, formatISO, parseISO } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import isEqual from 'lodash.isequal';

// Double Tap Support
import { DoubleTapRow } from '../components/DoubleTapRow';
import { SortableHeader } from '../components/SortableHeader';

// Styled Components
import {
  TrStyled,
  TableContainerStyled,
  SubContainerFiltersStyled,
  TdStyled,
  SubContainerHead,
  ShowFiltersButton,
  ArrowBottomStyled,
  DropDownInputStyledBig,
  SearchableDropdownStyledBig,
  MultiSelectDropdownStyledBig,
  MaterialsCellStyled,
  InputCalendarStyled,
  ContainerButtonStyled,
  ContainerStyled,
  ContainerLoader,
  FiltersWithLoadingStyled,
  StateCellWrap,
  SimultaneousBadge,
} from './styles';
import {
  Th,
  Tbody,
  Title,
  Table,
  TextCell,
  TextHeader,
  InputRadioContainer,
  InputRadioStyled,
  InputCheckContainer,
  CheckBoxStyled,
} from '../components/MainStyledComponents';

// Components
import { CheckBox } from '../../../inputs/CheckBox';
import { PaginationButtons } from '../components/PaginationButtons';
import { SelectionBanner } from '../components/SelectionBanner';
import InputText from '../../../inputs/InputText';
import InputExpedient from '../../../inputs/InputExpedient';
import InputIndex from '../../../inputs/InputIndex';
import InputPK from '../../../inputs/InputPK';
import { DropDownVisibilityColumn } from '../components/DropDownVisibilityColumn';
import { createFlagColumn, exportIgnoreProps } from '../components/flagColumn';
import { useFlagScopeTexts } from '../../../../hooks/useFlagScopeTexts';
import { SpecialTag } from '../../SpecialTag';
import { ButtonIconBackground } from '../../../inputs/buttons/ButtonIconBackground';
import { ExternalCompaniesCell } from '../../ExternalCompaniesCell';

// Types
import { columnID, getColumnNames, getColumnId } from '../components/types';
import { tableProps, dataTable } from './types';
import {
  Options,
  onChangeValue,
} from '../../../inputs/DropDown/DropDownList/types';
import { ImperativeRefInput } from '../../../inputs/DropDown/DropDownInput/types';
import { ImperativeRefSearchableDropdown } from '../../../inputs/SearchableDropdown/types';
import {
  ImperativeRefCalendarInput,
  onChangeInputType,
} from '../../../inputs/Calendar/types';
import {
  ImperativeRefInputExpedient,
  onChangeInputExpedientTypes,
} from '../../../inputs/InputExpedient/types';
import {
  ImperativeRefInputIndex,
  onChangeInputIndexTypes,
} from '../../../inputs/InputIndex/types';
import {
  ImperativeRefInputPK,
  onChangeInputPKTypes,
} from '../../../inputs/InputPK/types';
import { variant, size } from '../../SpecialTag/types';
import { Order } from '../../../../types/orders';
import { Role } from '../../../../types/auth';

// Form Constants
import {
  formActionNaming,
  formActionValidationNaming,
} from '../../../forms/ActionForms/constants';
import { externalCompaniesNaming } from '../../tables/formTables/ExternalCompaniesSelectionTable/constants';

// Data
import { getTrackOptions } from '../../../../utils/catalogLabels';

// Mocks
import { mocksTable } from './mocks';

// Utils
import { roidFilterFunction, dateFilterFunction } from './utils';
import { formatRoidForDisplay } from '../utils/roidFormatter';
import { compressSessionStorage } from '../../../../utils/sessionStorage';

// Actions
import { setCursor } from '../../../../state/actions/ui/cursorTables';
import { actionTabsUnlockAll } from '../../../../state/actions/ui/actionTabs';

// Media
import {
  useIsLandscapeTablet,
  useIsPortraitTablet,
  useIsSmallDesktop,
} from '../../../../hooks/useMediaBreakPoints';
import DropDownInput from '../../../inputs/DropDown/DropDownInput';
import { ImperativeRefMultiSelect } from '../../../inputs/DropDown/MultiSelectDropdown/types';
import {
  getLaneCutFilterOptions,
  laneCutSelectionToBoolean,
  booleanToLaneCutSelection,
} from '../../../../utils/laneCut';
import {
  buildMaterialFilterOptions,
  expandMaterialFilterValues,
  collapseMaterialIdsToValues,
  getHasMaterialsFilterOptions,
  hasMaterialsSelectionToBoolean,
  booleanToHasMaterialsSelection,
} from '../../../../utils/actionMaterials';
import IncludeInactiveToggle from '../../../inputs/IncludeInactiveToggle';
import {
  generateRoidPadding,
  getStateOptions,
  getStateOptionsByRoleAndEntityType,
  getContainerTypeOptions,
} from '../../../../utils/order';
import { EMPTY_VALUE } from '../../../../constants/emptyValue';
import {
  filterAndSortMultimediaUsers,
  filterAndSortOperators,
} from '../../../../utils/multimediaUserFilters';
import {
  takeBase,
  TABLE_ENTITY_TYPES,
  CONTAINER_SCOPED_TABLE_IDS,
  tableIds,
  listingFiltersByRole,
} from '../types';
import { useServerPageSize } from '../utils/useServerPageSize';
import { useMirroredCursor } from '../utils/useMirroredCursor';

// Graphql
import { client } from '../../../../gql/client';
import { GET_ACTION_BY_ID } from '../../../../gql/queries/actions/actions';
import { GET_ORDER_BY_ID } from '../../../../gql/queries/orders/orders';

// Redux
import {
  getCursor,
  getIsLoadingData,
} from '../../../../state/selectors/ui/tables';
import {
  GET_CLASSIFICATION_BY_ID_OR_ROID,
  GET_SUB_CAPITOLS_BY_IDS,
} from '../../../../gql/queries/orders/predefinedData';
import { option } from '../InputRadioArrowTable/types';
import { Loader } from '../../../inputs/Loader';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';

// Columnas ordenables server-side → campo del backend (whitelist Action)
const SORTABLE_COLUMNS = {
  state: 'state',
  registerInitDate: 'registerInitDate',
  creationDate: 'creationDate',
  pkInit: 'pkInitKm',
  pkEnd: 'pkEndKm',
  track: 'track',
  // `direction` ya está en ACTION_SORTABLE_FIELDS del backend (espejo de
  // Accident y GenericOrder), así que ordenar por Sentido no cuesta backend.
  // `lane` NO está: su cabecera es plana a propósito.
  direction: 'direction',
} as const;
const DEFAULT_SORT_FIELD = 'registerInitDate';

/**
 * Por defecto la bandera marca la Order de la fila: la fila de actuación ya
 * lleva su id (`selectActionOrders` → `order.id`; el de la Action va en
 * `actionID`). Fuera del componente para que la referencia sea estable y no
 * rehaga las columnas en cada render.
 */
const defaultGetFlagOrderId = (row: dataTable): string | undefined =>
  row.id || undefined;

export const ValidationsTable = (allProps: tableProps): JSX.Element => {
  const {
    titleText,
    data,
    onReFetchData,
    onReFetchPagination,
    tableId,
    // Props custom de selección que se leen abajo via `allProps as any`.
    // Las desestructuramos aquí para que NO se spreadeen sobre <Table {...props}>
    // (que renderiza un <table> HTML) y así evitar warnings tipo
    // "Unknown event handler property `onRowSelectionChange`. It will be ignored."
    selectionMode: _selectionMode,
    rowSelection: _rowSelection,
    onRowSelectionChange: _onRowSelectionChange,
    selectAllMode: _selectAllMode,
    excludedIds: _excludedIds,
    onSelectAll: _onSelectAll,
    onClearSelection: _onClearSelection,
    selectedCount: _selectedCount,
    setRowState: _setRowState,
    onRowOpen,
    // Fuera de `props`: no son atributos del <table>.
    showFlagColumn = false,
    showOnlyFlaggedFilter = false,
    getFlagOrderId = defaultGetFlagOrderId,
    ...props
  } = allProps as tableProps & Record<string, unknown>;

  const isCheckboxMode = allProps.selectionMode === 'checkbox';

  // Checkbox mode: controlled from parent (no internal rowSelection state)
  const controlledRowSelection = isCheckboxMode
    ? (allProps as any).rowSelection
    : undefined;
  const controlledOnChange = isCheckboxMode
    ? (allProps as any).onRowSelectionChange
    : undefined;
  const selectAllMode = isCheckboxMode
    ? ((allProps as any).selectAllMode ?? false)
    : false;
  const excludedIds = isCheckboxMode
    ? (allProps as any).excludedIds
    : undefined;
  const onSelectAll = isCheckboxMode
    ? (allProps as any).onSelectAll
    : undefined;
  const onClearSelection = isCheckboxMode
    ? (allProps as any).onClearSelection
    : undefined;
  const propsSelectedCount = isCheckboxMode
    ? (allProps as any).selectedCount
    : 0;

  // Radio mode: setRowState callback
  const radioSetRowState = !isCheckboxMode
    ? (allProps as any).setRowState
    : undefined;
  const { t } = useTranslation(['tables', 'common', 'forms']);
  const flagScopeTexts = useFlagScopeTexts();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const globalCursor = useSelector(getCursor(tableId as string), isEqual);
  const isLoadingData = useSelector(
    getIsLoadingData(tableId as string),
    isEqual
  );
  const dataAll = data?.actions ?? mocksTable;
  const roleActive = data?.roleActive;
  // Fase 4 nav Trabajo/Revisión (2026-07-12) — la columna+filtro "Origen" es
  // redundante en las pestañas CONTEXTUALES (campaña/RV). Se deriva del
  // `tableId` (no una prop nueva) — mismo criterio que `TABLE_ENTITY_TYPES`.
  const isContainerScopedTable = CONTAINER_SCOPED_TABLE_IDS.has(
    tableId as string
  );
  // Feedback usuario (2026-07-14): en RV los operarios van ligados al
  // RECORRIDO (RV.userIDs), no a sus actuaciones (que solo los HEREDAN al
  // crearse) — el filtro y la columna "Operarios" no aplican en la pestaña
  // Actuaciones del módulo RV. Derivado del tableId, mismo criterio que
  // isContainerScopedTable.
  const isRvScopedTable = (tableId as string) === tableIds.RV_ACTIONS;
  const inputListConRef = useRef<ImperativeRefInput>(null);
  const inputListCapitolRef = useRef<ImperativeRefSearchableDropdown>(null);
  const inputListSubcapitolRef = useRef<ImperativeRefSearchableDropdown>(null);
  const inputListClassificationRef =
    useRef<ImperativeRefSearchableDropdown>(null);
  const inputListTrackRef = useRef<ImperativeRefSearchableDropdown>(null);
  const inputListActivityRef = useRef<ImperativeRefSearchableDropdown>(null);
  const inputExternalCompanyRef = useRef<ImperativeRefSearchableDropdown>(null);
  const inputListPredefinedRef = useRef<ImperativeRefSearchableDropdown>(null);
  const inputStateRef = useRef<ImperativeRefMultiSelect>(null);
  const inputLaneCutRef = useRef<ImperativeRefInput>(null);
  const inputMaterialsRef = useRef<ImperativeRefMultiSelect>(null);
  const inputHasMaterialsRef = useRef<ImperativeRefInput>(null);
  const inputUserRef = useRef<ImperativeRefMultiSelect>(null);
  const inputOperatorRef = useRef<ImperativeRefMultiSelect>(null);
  // Fase 4 nav Trabajo/Revisión (2026-07-12) — filtro "Origen".
  const inputContainerTypeRef = useRef<ImperativeRefMultiSelect>(null);
  const inputTeamRef = useRef<ImperativeRefMultiSelect>(null);
  const inputDateRef = useRef<ImperativeRefCalendarInput>(null);
  const inputExpedientRef = useRef<ImperativeRefInputExpedient>(null);
  const inputIndexRef = useRef<ImperativeRefInputIndex>(null);
  const inputPKInitRef = useRef<ImperativeRefInputPK>(null);
  const inputPKEndRef = useRef<ImperativeRefInputPK>(null);

  const isSmallDesktop = useIsSmallDesktop();
  const isLanscapeTablet = useIsLandscapeTablet();
  const isPortraitTablet = useIsPortraitTablet();

  const showCapitolFilter = useFeatureFlag('filter_show_capitol', true);
  const showSubCapitolFilter = useFeatureFlag('filter_show_subcapitol', true);
  const showPredefinedFilter = useFeatureFlag('filter_show_predefined', true);

  const [expedientDefault, setExpedientDefault] = useState<number | undefined>(
    undefined
  );
  const [indexDefault, setIndexDefault] = useState<number | undefined>(
    undefined
  );
  const [showFilters, setShowFilters] = useState(false);
  // In checkbox mode: controlled from parent. In radio mode: internal state.
  const [internalRowSelection, setInternalRowSelection] = useState({});
  const rowSelection = isCheckboxMode
    ? controlledRowSelection
    : internalRowSelection;
  const setRowSelection = isCheckboxMode
    ? controlledOnChange
    : setInternalRowSelection;
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  // C-11.7: persistido en Redux para mantener coherencia paginador↔cursor
  // al navegar a un detail y volver. Ver hooks/useTableCurrentPage.
  const [currentServerPage, setCurrentServerPage] = useTableCurrentPage(
    tableId as string
  );
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: takeBase,
  });

  const initialSubCapitolsOptions = data?.filtersDataOptions?.subCapitols?.map(
    (subCapitol: { name: string; id: string }) => ({
      label: subCapitol.name,
      value: subCapitol.id,
    })
  );

  const [subCapitolOptions, setSubCapitolOptions] = useState(
    initialSubCapitolsOptions
  );

  const [selectorCursor, setSelectorCursor] = useMirroredCursor<{
    description: string | undefined; // filtro por descripción (texto libre)
    take: number;
    cursor: string;
    indexType: number | undefined;
    indexExpedition: number | undefined;
    concessionId: string | undefined;
    fromDate: string | undefined;
    toDate: string | undefined;
    classification: string | undefined;
    track: string | undefined;
    activityId: string | undefined;
    capitolId: string | undefined;
    subCapitolId: string | undefined;
    predefinedOrderId: string | undefined;
    state: string | undefined; // legacy single (kept for back-compat with other tables sharing this cursor shape)
    states: string[] | undefined; // multi-select (Plan 2 Tarea: filtro estado multi-select)
    userIds: string[] | undefined; // multi-select usuario (NEW filter — Plan 3 Tarea: filtro usuario en Actuaciones)
    operatorIds: string[] | undefined; // multi-select operarios (NUEVO: operarios asignados via Action.userIDs hasSome)
    containerTypes: string[] | undefined; // multi-select "Origen" (Fase 4 nav Trabajo/Revisión)
    teamIds: string[] | undefined; // multi-select equipo (heredado del expediente padre)
    includeInactive: boolean | undefined; // toggle "Incluir inactivos en Creador y Operarios" (Plan Task 5)
    // `undefined` = no lo ha tocado (manda el cursor) · `null` = eligió
    // «Todos» a propósito (limpia el filtro) · `true`/`false` = filtra.
    laneCutting: boolean | null | undefined;
    materialIds: string[] | undefined;
    hasMaterials: boolean | null | undefined;
    pkInitKm: number | undefined;
    pkInitMeter: number | undefined;
    pkEndKm: number | undefined;
    pkEndMeter: number | undefined;
    externalCompanyId: string | undefined;
    sortField: string | undefined;
    orderBy: 'asc' | 'desc' | undefined;
    onlyFlagged: true | undefined; // «Solo marcadas»: true o undefined, nunca false
  }>(globalCursor);

  const [selectorData, setSelectorData] = useState<{
    description: string | undefined; // filtro por descripción (texto libre)
    indexType: number | undefined;
    indexExpedition: number | undefined;
    concessionId: string | undefined;
    fromDate: string | undefined;
    toDate: string | undefined;
    classification: string | undefined;
    track: string | undefined;
    activityId: string | undefined;
    capitolId: string | undefined;
    subCapitolId: string | undefined;
    predefinedOrderId: string | undefined;
    state: string | undefined; // legacy single (kept for back-compat)
    states: string[] | undefined; // multi-select
    userIds: string[] | undefined; // multi-select usuario (NEW filter)
    operatorIds: string[] | undefined; // multi-select operarios (NUEVO)
    containerTypes: string[] | undefined; // multi-select "Origen" (Fase 4 nav Trabajo/Revisión)
    teamIds: string[] | undefined; // multi-select equipo (heredado del expediente padre)
    includeInactive: boolean | undefined; // toggle "Incluir inactivos en Creador y Operarios" (Plan Task 5)
    // `undefined` = no lo ha tocado (manda el cursor) · `null` = eligió
    // «Todos» a propósito (limpia el filtro) · `true`/`false` = filtra.
    laneCutting: boolean | null | undefined;
    materialIds: string[] | undefined;
    hasMaterials: boolean | null | undefined;
    pkInitKm: number | undefined;
    pkInitMeter: number | undefined;
    pkEndKm: number | undefined;
    pkEndMeter: number | undefined;
    externalCompanyId: string | undefined;
  }>({
    description: undefined,
    indexType: undefined,
    indexExpedition: undefined,
    concessionId: undefined,
    fromDate: undefined,
    toDate: undefined,
    classification: undefined,
    track: undefined,
    activityId: undefined,
    capitolId: undefined,
    subCapitolId: undefined,
    predefinedOrderId: undefined,
    state: undefined,
    states: undefined,
    userIds: undefined,
    operatorIds: undefined,
    containerTypes: undefined,
    teamIds: undefined,
    includeInactive: undefined,
    laneCutting: undefined,
    materialIds: undefined,
    hasMaterials: undefined,
    pkInitKm: undefined,
    pkInitMeter: undefined,
    pkEndKm: undefined,
    pkEndMeter: undefined,
    externalCompanyId: undefined,
  });

  // Tarea #11 — page-size selector. ValidationsTable se usa en 4 listados
  // principales (REPORTING_ACTIONS, GENERAL_ACTIONS, VALIDATION_ACTIONS,
  // SEARCH_AND_CREATE_HISTORICAL_ACTIONS), nunca en modal — `enabled=true`.
  // `liveFilters: selectorData` para respetar filtros del form aún no
  // comprometidos a Redux al cambiar el take.
  const { takePagination, paginationProps } = useServerPageSize({
    tableId: tableId as string,
    enabled: true,
    selectorCursor,
    liveFilters: selectorData as unknown as Record<string, unknown>,
    setCurrentServerPage,
    onReFetchData,
  });

  const handleClick = useCallback(() => {
    setShowFilters(!showFilters);
  }, [showFilters]);

  useEffect(() => {
    isLanscapeTablet ? setShowFilters(false) : setShowFilters(true);
  }, [isLanscapeTablet]);

  // NOTE: ValidationsTable is used for Actions (OrderType.Action)
  // Actions should NOT apply default state filter - they show ALL states
  // See BUG #11 in TRACKING_BUGS_TESTING_2026.md

  const callbackSetRoid = useCallback(async () => {
    let classification;
    if (selectorCursor?.classification) {
      const classificationRaw = await client.query({
        query: GET_CLASSIFICATION_BY_ID_OR_ROID,
        variables: {
          id: selectorCursor?.classification,
        },
      });
      classification =
        classificationRaw?.data?.classification?.classificationCode;
    }
    const roidData = [
      '',
      selectorCursor?.indexType
        ? selectorCursor?.indexType >= 0
          ? generateRoidPadding(selectorCursor?.indexType, 4)
          : ''
        : '',
      selectorCursor?.indexExpedition
        ? selectorCursor?.indexExpedition >= 0
          ? generateRoidPadding(selectorCursor?.indexExpedition, 7)
          : ''
        : '',
      classification ? generateRoidPadding(classification, 2) : '',
    ];
    setExpedientDefault(
      selectorCursor?.indexExpedition !== undefined &&
        selectorCursor?.indexExpedition >= 0
        ? selectorCursor.indexExpedition
        : undefined
    );
    setIndexDefault(
      selectorCursor?.indexType !== undefined && selectorCursor?.indexType >= 0
        ? selectorCursor.indexType
        : undefined
    );
  }, [selectorCursor]);

  useEffect(() => {
    callbackSetRoid();
  }, [selectorCursor]);

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({
    containerType: !isContainerScopedTable,
  });

  useEffect(() => {
    setColumnVisibility((prev) => ({
      ...prev,
      predefinedOrder: showPredefinedFilter,
      capitol: showCapitolFilter,
      subCapitol: showSubCapitolFilter,
    }));
  }, [showPredefinedFilter, showCapitolFilter, showSubCapitolFilter]);

  useEffect(() => {
    const concessions = data?.filtersDataOptions?.concessions;
    setColumnVisibility((prev) => ({
      ...prev,
      concession: !concessions || concessions.length > 1,
    }));
  }, [data?.filtersDataOptions?.concessions]);

  const [sizeTag, setSizeTag] = useState<size>(
    isPortraitTablet ? 'circle' : 'small'
  );

  useLayoutEffect(() => {
    setSizeTag(isPortraitTablet ? 'circle' : 'small');
  }, [isPortraitTablet]);

  const columnHelper = createColumnHelper<dataTable>();
  const translatedColumnNames = useMemo(() => getColumnNames(t), [t]);

  // Ordenación server-side: campo activo y dirección derivados del cursor Redux
  const activeSortField = selectorCursor?.sortField ?? DEFAULT_SORT_FIELD;
  const activeSortColumnId =
    (Object.keys(SORTABLE_COLUMNS) as (keyof typeof SORTABLE_COLUMNS)[]).find(
      (k) => SORTABLE_COLUMNS[k] === activeSortField
    ) ?? 'registerInitDate';
  const sortDirection: 'asc' | 'desc' =
    selectorCursor?.orderBy === 'asc' ? 'asc' : 'desc';

  const onSortColumn = useCallback(
    (sortField: string) => {
      const isActive = activeSortField === sortField;
      const nextOrderBy: 'asc' | 'desc' =
        isActive && sortDirection === 'desc' ? 'asc' : 'desc';
      // Canonicalización: registerInitDate + desc viaja como sortField: undefined EXPLÍCITO
      const isDefault =
        sortField === DEFAULT_SORT_FIELD && nextOrderBy === 'desc';
      const dataCursor = {
        sortField: isDefault ? undefined : sortField,
        orderBy: nextOrderBy,
        cursor: '',
      };
      dispatch(setCursor(tableId as string, dataCursor));
      setCurrentServerPage(1);
      onReFetchData?.();
    },
    [
      activeSortField,
      sortDirection,
      dispatch,
      tableId,
      setCurrentServerPage,
      onReFetchData,
    ]
  );

  // Helper que evita la duplicación en las columnas ordenables.
  const sortableHeader = useCallback(
    (columnId: keyof typeof SORTABLE_COLUMNS) => () => (
      <SortableHeader
        label={translatedColumnNames[columnId]?.label ?? columnId}
        active={activeSortColumnId === columnId}
        direction={sortDirection}
        onToggle={() => onSortColumn(SORTABLE_COLUMNS[columnId])}
      />
    ),
    [translatedColumnNames, activeSortColumnId, sortDirection, onSortColumn]
  );

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'select',
        ...(isCheckboxMode
          ? {
              header: ({ table }) => (
                <InputCheckContainer>
                  <CheckBox
                    name={'select'}
                    {...{
                      checked: table.getIsAllRowsSelected(),
                      indeterminate: table.getIsSomeRowsSelected(),
                      onChange: table.getToggleAllRowsSelectedHandler(),
                    }}
                  />
                </InputCheckContainer>
              ),
              cell: ({ row }) => (
                <InputCheckContainer
                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                >
                  <CheckBoxStyled
                    {...{
                      checked: row.getIsSelected(),
                      disabled: !row.getCanSelect(),
                      indeterminate: row.getIsSomeSelected(),
                      onChange: row.getToggleSelectedHandler(),
                    }}
                  />
                </InputCheckContainer>
              ),
            }
          : {
              cell: ({ row }) => (
                <InputRadioContainer
                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                >
                  <InputRadioStyled
                    name="inputRadio"
                    {...{
                      checked: row.getIsSelected(),
                      disabled: !row.getCanSelect(),
                      indeterminate: row.getIsSomeSelected(),
                      onChange: row.getToggleSelectedHandler(),
                    }}
                  />
                </InputRadioContainer>
              ),
            }),
      }),
      // Banderitas (spec 2026-10-22 § 8.2): justo detrás de la selección y
      // solo si la pantalla la pide.
      ...(showFlagColumn
        ? [
            createFlagColumn<dataTable>(
              getFlagOrderId),
          ]
        : []),
      columnHelper.accessor(getColumnId(translatedColumnNames.state.id), {
        header: sortableHeader('state'),
        cell: (info) => (
          <StateCellWrap>
            <SpecialTag
              sizeTag={sizeTag}
              variant={info.getValue() as variant}
            />
            {info.row.original.isSimultaneous && (
              <SimultaneousBadge title={t('common:simultaneous')}>
                {t('common:simultaneous')}
              </SimultaneousBadge>
            )}
          </StateCellWrap>
        ),
      }),
      columnHelper.accessor(getColumnId(translatedColumnNames.roid.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {translatedColumnNames.roid.label}
          </TextHeader>
        ),
        cell: (info) => (
          <TextCell size={'S'}>
            {formatRoidForDisplay(info.getValue())}
          </TextCell>
        ),
        filterFn: 'roidFilter' as FilterFnOption<Order>, //Indicate the filter function to be used for this column
      }),
      // Fecha de inicio: misma columna `registerInitDate` (sorting + dateFilter
      // intactos), pero etiquetada "Fecha de inicio" SOLO en la tabla de
      // actuaciones — el registro compartido sigue siendo "Fecha de registro"
      // para órdenes/accidentes/etc.
      columnHelper.accessor(
        getColumnId(translatedColumnNames.registerInitDate.id),
        {
          // Combina: ordenable (sorting) + etiqueta "Fecha de inicio" propia de
          // la tabla de actuaciones (#60 action-dates). Inline en vez de
          // sortableHeader('registerInitDate') porque el label difiere del
          // genérico translatedColumnNames.registerInitDate.label.
          header: () => (
            <SortableHeader
              label={t('forms:workOrder.startDate')}
              active={activeSortColumnId === 'registerInitDate'}
              direction={sortDirection}
              onToggle={() => onSortColumn(SORTABLE_COLUMNS.registerInitDate)}
            />
          ),
          cell: (info) => (
            <TextCell size={'S'}>
              {info.getValue()
                ? format(
                    new Date(info.getValue() as string),
                    'dd/MM/yyyy - HH:mm'
                  )
                : '-'}
            </TextCell>
          ),
          filterFn: 'dateFilter' as FilterFnOption<dataTable>,
        }
      ),
      columnHelper.accessor(
        getColumnId(translatedColumnNames.registerEndDate.id),
        {
          header: () => (
            <TextHeader size={'XS'} colorText={'greyDark'}>
              {translatedColumnNames.registerEndDate.label}
            </TextHeader>
          ),
          cell: (info) => (
            <TextCell size={'S'}>
              {info.getValue()
                ? format(
                    new Date(info.getValue() as string),
                    'dd/MM/yyyy - HH:mm'
                  )
                : '-'}
            </TextCell>
          ),
        }
      ),
      columnHelper.accessor(
        getColumnId(translatedColumnNames.registerArrivalDate.id),
        {
          header: () => (
            <TextHeader size={'XS'} colorText={'greyDark'}>
              {translatedColumnNames.registerArrivalDate.label}
            </TextHeader>
          ),
          cell: (info) => (
            <TextCell size={'S'}>
              {info.getValue()
                ? format(
                    new Date(info.getValue() as string),
                    'dd/MM/yyyy - HH:mm'
                  )
                : '-'}
            </TextCell>
          ),
        }
      ),

      columnHelper.accessor(getColumnId(translatedColumnNames.capitol.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {translatedColumnNames.capitol.label}
          </TextHeader>
        ),
        cell: (info) => (
          <TextCell size={'S'}>{info.getValue() as string}</TextCell>
        ),
      }),

      columnHelper.accessor(getColumnId(translatedColumnNames.subCapitol.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {translatedColumnNames.subCapitol.label}
          </TextHeader>
        ),
        cell: (info) => (
          <TextCell size={'S'}>{info.getValue() as string}</TextCell>
        ),
      }),

      columnHelper.accessor(
        getColumnId(translatedColumnNames.predefinedOrder.id),
        {
          header: () => (
            <TextHeader size={'XS'} colorText={'greyDark'}>
              {translatedColumnNames.predefinedOrder.label}
            </TextHeader>
          ),
          cell: (info) => (
            <TextCell size={'S'}>{info.getValue() as string}</TextCell>
          ),
        }
      ),

      columnHelper.accessor(getColumnId(translatedColumnNames.concession.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {translatedColumnNames.concession.label}
          </TextHeader>
        ),
        cell: (info) => (
          <TextCell size={'S'}>{info.getValue() as string}</TextCell>
        ),
      }),
      columnHelper.accessor(getColumnId(translatedColumnNames.road.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {translatedColumnNames.road.label}
          </TextHeader>
        ),
        cell: (info) => (
          <TextCell size={'S'}>{info.getValue() as string}</TextCell>
        ),
      }),
      // Sentido: mismo patrón que `AccidentsTable`. La celda pinta el valor
      // CRUDO — el formato "Texto (símbolo)" lo pone `selectActionOrders`.
      columnHelper.accessor(getColumnId(translatedColumnNames.direction.id), {
        header: sortableHeader('direction'),
        cell: (info) => (
          <TextCell size={'S'}>{info.getValue() as string}</TextCell>
        ),
      }),
      columnHelper.accessor(getColumnId(translatedColumnNames.track.id), {
        header: sortableHeader('track'),
        cell: (info) => (
          <TextCell size={'S'}>{info.getValue() as string}</TextCell>
        ),
      }),
      // Carril: cabecera plana. `lane` no está en la whitelist de ordenación
      // del backend, y meterlo exigiría una PR de `rois-back` + merge
      // back-first, que no compensa para agrupar cinco valores.
      columnHelper.accessor(getColumnId(translatedColumnNames.lane.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {translatedColumnNames.lane.label}
          </TextHeader>
        ),
        cell: (info) => (
          <TextCell size={'S'}>{info.getValue() as string}</TextCell>
        ),
      }),
      columnHelper.accessor(getColumnId(translatedColumnNames.user.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {translatedColumnNames.user.label}
          </TextHeader>
        ),
        cell: (info) => (
          <TextCell size={'S'}>{(info.getValue() as string) || ''}</TextCell>
        ),
      }),
      columnHelper.accessor(
        getColumnId(translatedColumnNames.registeredBy.id),
        {
          header: () => (
            <TextHeader size={'XS'} colorText={'greyDark'}>
              {translatedColumnNames.registeredBy.label}
            </TextHeader>
          ),
          cell: (info) => (
            <TextCell size={'S'}>{(info.getValue() as string) || ''}</TextCell>
          ),
        }
      ),
      // Columna Operarios — fuera en la pestaña Actuaciones del módulo RV
      // (los operarios son del RECORRIDO, feedback usuario 2026-07-14).
      ...(!isRvScopedTable
        ? [
            columnHelper.accessor(
              getColumnId(translatedColumnNames.operators.id),
              {
                header: () => (
                  <TextHeader size={'XS'} colorText={'greyDark'}>
                    {translatedColumnNames.operators.label}
                  </TextHeader>
                ),
                // La básica del RV no tiene tripulación (es del recorrido):
                // guion, como la columna Operarios del listado de recorridos.
                cell: (info) => {
                  const text = info.row.original.isBasicAction
                    ? EMPTY_VALUE
                    : (info.getValue() as string) || '';
                  return (
                    <TextCell size={'S'} title={text}>
                      {text}
                    </TextCell>
                  );
                },
              }
            ),
          ]
        : []),
      columnHelper.accessor(getColumnId(translatedColumnNames.activity.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {translatedColumnNames.activity.label}
          </TextHeader>
        ),
        cell: (info) => (
          <TextCell size={'S'}>{info.getValue() as string}</TextCell>
        ),
      }),
      columnHelper.accessor((row) => row.isLaneCut ?? false, {
        id: getColumnId(translatedColumnNames.laneCut.id),
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {translatedColumnNames.laneCut.label}
          </TextHeader>
        ),
        cell: (info) => (
          <TextCell size={'S'}>
            {info.getValue()
              ? t('forms:actionEdition.yes')
              : t('forms:actionEdition.no')}
          </TextCell>
        ),
      }),
      columnHelper.accessor(getColumnId(translatedColumnNames.materials.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {translatedColumnNames.materials.label}
          </TextHeader>
        ),
        // Reusa la celda de lista con truncado + tooltip. Se llama
        // `ExternalCompaniesCell` por su primer uso, pero es genérica: recibe
        // `string[]` y no sabe de empresas. Deuda anotada: renombrarla tocaría
        // las otras dos tablas que la usan.
        // El ancho va en la celda (`MaterialsCellStyled`), no en `size`: esta
        // tabla no usa layout fijo y el `size` de la columna no llega a
        // aplicarse — medido, con y sin él la columna daba los mismos 138 px.
        cell: (info) => (
          <MaterialsCellStyled
            companies={(info.getValue() as unknown as string[]) || []}
          />
        ),
      }),
      columnHelper.accessor(getColumnId(translatedColumnNames.pkInit.id), {
        header: sortableHeader('pkInit'),
        cell: (info) => (
          <TextCell size={'S'}>{`${info.getValue()} km`}</TextCell>
        ),
      }),
      columnHelper.accessor(getColumnId(translatedColumnNames.pkEnd.id), {
        header: sortableHeader('pkEnd'),
        cell: (info) => (
          <TextCell size={'S'}>{`${info.getValue()} km`}</TextCell>
        ),
      }),
      columnHelper.accessor(getColumnId(translatedColumnNames.description.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {translatedColumnNames.description.label}
          </TextHeader>
        ),
        cell: (info) => (
          <TextCell size={'S'}>{info.getValue() as string}</TextCell>
        ),
      }),
      columnHelper.accessor(
        getColumnId(translatedColumnNames.externalCompanies.id),
        {
          header: () => (
            <TextHeader size={'XS'} colorText={'greyDark'}>
              {translatedColumnNames.externalCompanies.label}
            </TextHeader>
          ),
          cell: (info) => (
            <ExternalCompaniesCell
              companies={(info.getValue() as unknown as string[]) || []}
            />
          ),
          size: 180,
        }
      ),
      // Fase 4 nav Trabajo/Revisión (2026-07-12) — columna "Origen"
      // (containerType traducido con la label EXISTENTE `common:orderTypes.*`,
      // servido directo por el backend — ver gql/fragments/action.ts).
      // Oculta por defecto en pestañas contextuales vía
      // `columnVisibility.origin` (gating por tableId).
      columnHelper.accessor(
        getColumnId(translatedColumnNames.containerType.id),
        {
          header: () => (
            <TextHeader size={'XS'} colorText={'greyDark'}>
              {translatedColumnNames.containerType.label}
            </TextHeader>
          ),
          cell: (info) => {
            const value = info.getValue() as string | undefined;
            return (
              <TextCell size={'S'}>
                {value ? t(`common:orderTypes.${value}`) : ''}
              </TextCell>
            );
          },
        }
      ),
      columnHelper.accessor(
        getColumnId(translatedColumnNames.creationDate.id),
        {
          header: sortableHeader('creationDate'),
          cell: (info) => (
            <TextCell size={'S'}>
              {format(
                new Date(info.getValue() as string),
                'dd/MM/yyyy - HH:mm'
              )}
            </TextCell>
          ),
        }
      ),
    ],
    [
      isPortraitTablet,
      sizeTag,
      translatedColumnNames,
      isRvScopedTable,
      t,
      sortableHeader,
      showFlagColumn,
      getFlagOrderId,
    ]
  );

  // Tarea #11 — Sincronizar TanStack pageSize con `takePagination` (server).
  const pagination = useMemo(
    () => ({
      pageIndex,
      pageSize: takePagination,
    }),
    [pageIndex, takePagination]
  );

  const filterFns = {
    roidFilter: roidFilterFunction,
    dateFilter: dateFilterFunction,
  };

  const table = useReactTable({
    getRowId: (row: any) => row.id,
    data: dataAll as Order[],
    columns,
    state: {
      rowSelection,
      columnFilters,
      pagination,
      columnVisibility,
    },
    filterFns,
    manualSorting: true,
    onColumnVisibilityChange: setColumnVisibility,
    enableRowSelection: true, //enable row selection for all rows
    enableMultiRowSelection: isCheckboxMode,
    onPaginationChange: setPagination,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getPaginationRowModel: getPaginationRowModel(),
  });

  const capitolOptions = data?.filtersDataOptions?.capitols.map((capitol) => ({
    label: capitol.name,
    value: capitol.id,
    subCapitolsIDs: capitol.subCapitolsIDs,
  }));

  const concessionOptions = data?.filtersDataOptions?.concessions.map(
    (concession) => ({
      label: concession.name,
      value: concession.id,
    })
  );

  const classificationOptions = data?.filtersDataOptions?.classifications
    ?.slice()
    ?.sort((a, b) => (a.classificationCode ?? 0) - (b.classificationCode ?? 0))
    ?.map((classification) => ({
      label: `${classification.classificationCode} - ${classification.name}`,
      value: classification.id,
    }));

  const trackOptions = useMemo(() => getTrackOptions(t), [t]);

  // Materiales del catálogo para el multi-select. Viene del loader
  // (`filtersDataOptions`), como el resto de catálogos de esta barra — nada de
  // consultar desde el componente.
  const materialFilterOptions = useMemo(
    () =>
      buildMaterialFilterOptions(
        (data?.filtersDataOptions as any)?.materials ?? []
      ),
    [data?.filtersDataOptions]
  );

  // Las opciones van agrupadas por nombre, así que un `value` puede llevar
  // varios ids. El cursor guarda los ids PLANOS (es lo que consume el
  // backend), de modo que hay que traducir de vuelta para saber qué casillas
  // pintar marcadas.
  const selectedMaterialValues = useMemo(
    () =>
      collapseMaterialIdsToValues(
        selectorCursor?.materialIds,
        materialFilterOptions
      ),
    [selectorCursor?.materialIds, materialFilterOptions]
  );

  const activityOptions = useMemo(
    () =>
      (data?.filtersDataOptions as any)?.activitys?.map(
        (activity: { id: string; name: string }) => ({
          label: activity.name,
          value: activity.id,
        })
      ) ?? [],
    [data?.filtersDataOptions]
  );

  // Team options for filtering
  const teamOptions = data?.filtersDataOptions?.teams?.map((team) => ({
    label: team.name,
    value: team.id,
  }));

  // T64: External company options for filtering
  const externalCompanyOptions =
    data?.filtersDataOptions?.externalCompanies?.map(
      (company: { id: string; companyName: string }) => ({
        label: company.companyName,
        value: company.id,
      })
    );

  // Predefined order options for filtering
  const predefinedOrderOptions =
    data?.filtersDataOptions?.predefinedOrders?.map(
      (predefined: { id: string; name: string }) => ({
        label: predefined.name,
        value: predefined.id,
      })
    );

  // User options for filtering (multi-select). Reusa la lógica de Multimedia
  // (PR #22): usuarios con rol {SuperAdmin, Coex, RoomTeam, Tablet} —
  // operarios externos y desactivados quedan fuera del dropdown.
  // Ordenados alfabéticamente con localeCompare('es').
  //
  // Plan Task 5: cuando `includeInactive=false` pero el cursor ya tiene
  // userIds seleccionados que NO están en la base activa (porque están
  // inactivos), se mergean como missing options para que los chips
  // sigan renderizando (edge case 7.1 del spec).
  const userOptions = useMemo(() => {
    const base = filterAndSortMultimediaUsers(
      data?.filtersDataOptions?.users,
      selectorCursor?.includeInactive ?? false
    );
    const selectedIds = selectorCursor?.userIds ?? [];
    const missingIds = selectedIds.filter(
      (id: string) => !base.find((o) => o.value === id)
    );
    if (missingIds.length === 0) return base;
    const allUsers = data?.filtersDataOptions?.users ?? [];
    const missingOptions = missingIds
      .map((id: string) => allUsers.find((u: { id: string }) => u.id === id))
      .filter(
        (
          u: unknown
        ): u is {
          id?: string;
          name?: string;
          surName?: string;
          isActive?: boolean;
        } => Boolean(u)
      )
      .map((u) => ({
        label:
          u?.isActive === false
            ? `${u?.name ?? ''} ${u?.surName ?? ''} (inactivo)`.trim()
            : `${u?.name ?? ''} ${u?.surName ?? ''}`.trim(),
        value: u?.id as string,
      }));
    return [...base, ...missingOptions];
  }, [
    data?.filtersDataOptions?.users,
    selectorCursor?.includeInactive,
    selectorCursor?.userIds,
  ]);

  // Operarios (userType='operator' + activos) para el dropdown "Operarios"
  // separado del filtro "Usuario" (creador). Mismo source `users` ya
  // pre-cargado por el loader, sin query adicional.
  //
  // Plan Task 5: mismo patrón missing-id merge que userOptions, pero
  // contra operatorIds (no userIds).
  const operatorOptions = useMemo(() => {
    const base = filterAndSortOperators(
      data?.filtersDataOptions?.users,
      selectorCursor?.includeInactive ?? false
    );
    const selectedIds = selectorCursor?.operatorIds ?? [];
    const missingIds = selectedIds.filter(
      (id: string) => !base.find((o) => o.value === id)
    );
    if (missingIds.length === 0) return base;
    const allUsers = data?.filtersDataOptions?.users ?? [];
    const missingOptions = missingIds
      .map((id: string) => allUsers.find((u: { id: string }) => u.id === id))
      .filter(
        (
          u: unknown
        ): u is {
          id?: string;
          name?: string;
          surName?: string;
          isActive?: boolean;
        } => Boolean(u)
      )
      .map((u) => ({
        label:
          u?.isActive === false
            ? `${u?.name ?? ''} ${u?.surName ?? ''} (inactivo)`.trim()
            : `${u?.name ?? ''} ${u?.surName ?? ''}`.trim(),
        value: u?.id as string,
      }));
    return [...base, ...missingOptions];
  }, [
    data?.filtersDataOptions?.users,
    selectorCursor?.includeInactive,
    selectorCursor?.operatorIds,
  ]);

  // Auto-select the only concession if there's only one available
  useEffect(() => {
    if (
      concessionOptions &&
      concessionOptions.length === 1 &&
      !selectorCursor.concessionId
    ) {
      const onlyConcession = concessionOptions[0];
      setSelectorCursor((prev) => ({
        ...prev,
        concessionId: onlyConcession.value,
      }));
      setSelectorData((prev) => ({
        ...prev,
        concessionId: onlyConcession.value,
      }));
    }
  }, [concessionOptions, selectorCursor.concessionId]);

  //I select the column and we pass the filter with the input values
  const onChangeInputExpedient = useCallback<onChangeInputExpedientTypes>(
    (value) => {
      setExpedientDefault(value);
      setSelectorData((prevData) => ({
        ...prevData,
        indexExpedition: value,
      }));
    },
    []
  );

  const onChangeInputIndex = useCallback<onChangeInputIndexTypes>((value) => {
    setIndexDefault(value);
    setSelectorData((prevData) => ({
      ...prevData,
      indexType: value,
    }));
  }, []);

  const onChangePKInit = useCallback<onChangeInputPKTypes>((km, meter) => {
    setSelectorData((prevData) => ({
      ...prevData,
      pkInitKm: km,
      pkInitMeter: meter,
    }));
  }, []);

  const onChangePKEnd = useCallback<onChangeInputPKTypes>((km, meter) => {
    setSelectorData((prevData) => ({
      ...prevData,
      pkEndKm: km,
      pkEndMeter: meter,
    }));
  }, []);

  const onChangeSelectedClassification = (value: option) => {
    setSelectorData((prevData) => {
      const newData = { ...prevData };
      newData.classification = value.value;
      return newData;
    });
  };

  const onChangeSelectedTrack = (value: option) => {
    setSelectorData((prevData) => {
      const newData = { ...prevData };
      newData.track = value.value;
      return newData;
    });
  };

  const onChangeSelectedActivity = (value: option) => {
    setSelectorData((prevData) => {
      const newData = { ...prevData };
      newData.activityId = value.value;
      return newData;
    });
  };

  /**
   * *Function for filtering date ranges
   * @param range is a range of dates ({from, to})
   */
  const onChangeInput = useCallback<onChangeInputType>(
    (range) => {
      if (range.to) {
        range.to.setHours(23);
        range.to.setMinutes(59);
        range.to.setSeconds(59);
        range.to.setMilliseconds(999);
      }
      const fromDateIso = formatISO(range.from as Date);
      const toDateIso = formatISO(range.to as Date);
      const dataCursor = {
        take: selectorCursor.take,
        cursor: selectorCursor?.cursor,
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        subCapitolId: selectorData.subCapitolId ?? selectorCursor.subCapitolId,
        capitolId: selectorData.capitolId ?? selectorCursor.capitolId,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        fromDate: fromDateIso,
        toDate: toDateIso,
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        predefinedOrderId:
          selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
        states: selectorData?.states ?? selectorCursor?.states,
        userIds: selectorData?.userIds ?? selectorCursor?.userIds,
        operatorIds: selectorData?.operatorIds ?? selectorCursor?.operatorIds,
        containerTypes:
          selectorData?.containerTypes ?? selectorCursor?.containerTypes,
        teamIds: selectorData?.teamIds ?? selectorCursor?.teamIds,
        includeInactive:
          selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        pkInitKm: selectorData?.pkInitKm ?? selectorCursor?.pkInitKm,
        pkInitMeter: selectorData?.pkInitMeter ?? selectorCursor?.pkInitMeter,
        pkEndKm: selectorData?.pkEndKm ?? selectorCursor?.pkEndKm,
        pkEndMeter: selectorData?.pkEndMeter ?? selectorCursor?.pkEndMeter,
        track: selectorData?.track ?? selectorCursor?.track,
        activityId: selectorData?.activityId ?? selectorCursor?.activityId,
        externalCompanyId:
          selectorData?.externalCompanyId ?? selectorCursor?.externalCompanyId,
      };
      dispatch(setCursor(tableId as string, dataCursor));
      setSelectorData((prevData) => ({
        ...prevData,
        fromDate: fromDateIso,
        toDate: toDateIso,
      }));
    },
    [
      selectorCursor,
      selectorData?.indexType,
      selectorData?.indexExpedition,
      selectorData?.concessionId,
      selectorData?.classification,
      selectorData?.predefinedOrderId,
      selectorData?.states,
      selectorData?.userIds,
      selectorData?.capitolId,
      selectorData?.subCapitolId,
      selectorData?.pkInitKm,
      selectorData?.pkEndKm,
    ]
  );

  const onChangeSelectedCapitolGroup = useCallback(
    async (option: option) => {
      const {
        data: { readSubCapitols },
      } = await client.query({
        query: GET_SUB_CAPITOLS_BY_IDS,
        variables: {
          ids: option.subCapitolsIDs,
        },
      });
      const sortedSubCapitols = [...readSubCapitols].sort((a, b) =>
        a.name.localeCompare(b.name)
      );
      setSubCapitolOptions(
        sortedSubCapitols?.map((road: { id: string; name: string }) => ({
          value: road.id,
          label: road.name,
        }))
      );
      const dataCursor = {
        take: selectorCursor.take,
        cursor: selectorCursor?.cursor,
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        subCapitolId: undefined,
        capitolId: option.value,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        predefinedOrderId:
          selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
        states: selectorData?.states ?? selectorCursor?.states,
        userIds: selectorData?.userIds ?? selectorCursor?.userIds,
        operatorIds: selectorData?.operatorIds ?? selectorCursor?.operatorIds,
        containerTypes:
          selectorData?.containerTypes ?? selectorCursor?.containerTypes,
        teamIds: selectorData?.teamIds ?? selectorCursor?.teamIds,
        includeInactive:
          selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        pkInitKm: selectorData?.pkInitKm ?? selectorCursor?.pkInitKm,
        pkInitMeter: selectorData?.pkInitMeter ?? selectorCursor?.pkInitMeter,
        pkEndKm: selectorData?.pkEndKm ?? selectorCursor?.pkEndKm,
        pkEndMeter: selectorData?.pkEndMeter ?? selectorCursor?.pkEndMeter,
        track: selectorData?.track ?? selectorCursor?.track,
        activityId: selectorData?.activityId ?? selectorCursor?.activityId,
        externalCompanyId:
          selectorData?.externalCompanyId ?? selectorCursor?.externalCompanyId,
      };
      dispatch(setCursor(tableId as string, dataCursor));
      setSelectorData((prevData) => ({
        ...prevData,
        capitolId: option.value,
        subCapitolId: undefined,
      }));

      inputListSubcapitolRef.current?.clearSelected();
    },
    [
      selectorCursor,
      selectorData?.indexType,
      selectorData?.indexExpedition,
      selectorData?.concessionId,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData?.classification,
      selectorData?.predefinedOrderId,
      selectorData?.states,
      selectorData?.userIds,
      selectorData?.pkInitKm,
      selectorData?.pkEndKm,
    ]
  );

  const onChangeSelectedSubcapitolGroup = useCallback(
    (option: option) => {
      const dataCursor = {
        take: selectorCursor.take,
        cursor: selectorCursor?.cursor,
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        subCapitolId: option.value,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        capitolId: selectorData?.capitolId ?? selectorCursor?.capitolId,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        predefinedOrderId:
          selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
        states: selectorData?.states ?? selectorCursor?.states,
        userIds: selectorData?.userIds ?? selectorCursor?.userIds,
        operatorIds: selectorData?.operatorIds ?? selectorCursor?.operatorIds,
        containerTypes:
          selectorData?.containerTypes ?? selectorCursor?.containerTypes,
        teamIds: selectorData?.teamIds ?? selectorCursor?.teamIds,
        includeInactive:
          selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        pkInitKm: selectorData?.pkInitKm ?? selectorCursor?.pkInitKm,
        pkInitMeter: selectorData?.pkInitMeter ?? selectorCursor?.pkInitMeter,
        pkEndKm: selectorData?.pkEndKm ?? selectorCursor?.pkEndKm,
        pkEndMeter: selectorData?.pkEndMeter ?? selectorCursor?.pkEndMeter,
        track: selectorData?.track ?? selectorCursor?.track,
        activityId: selectorData?.activityId ?? selectorCursor?.activityId,
        externalCompanyId:
          selectorData?.externalCompanyId ?? selectorCursor?.externalCompanyId,
      };
      dispatch(setCursor(tableId as string, dataCursor));
      setSelectorData((prevData) => ({
        ...prevData,
        subCapitolId: option.value,
      }));
    },
    [
      selectorCursor,
      selectorData?.indexType,
      selectorData?.indexExpedition,
      selectorData?.concessionId,
      selectorData?.capitolId,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData?.classification,
      selectorData?.predefinedOrderId,
      selectorData?.states,
      selectorData?.userIds,
      selectorData?.pkInitKm,
      selectorData?.pkEndKm,
    ]
  );

  const onChangeSelectedConcession = useCallback(
    (option: option) => {
      const dataCursor = {
        take: selectorCursor.take,
        cursor: selectorCursor?.cursor,
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        subCapitolId:
          selectorData?.subCapitolId ?? selectorCursor?.subCapitolId,
        capitolId: selectorData?.capitolId ?? selectorCursor?.capitolId,
        concessionId: option.value,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        predefinedOrderId:
          selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
        states: selectorData?.states ?? selectorCursor?.states,
        userIds: selectorData?.userIds ?? selectorCursor?.userIds,
        operatorIds: selectorData?.operatorIds ?? selectorCursor?.operatorIds,
        containerTypes:
          selectorData?.containerTypes ?? selectorCursor?.containerTypes,
        teamIds: selectorData?.teamIds ?? selectorCursor?.teamIds,
        includeInactive:
          selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        pkInitKm: selectorData?.pkInitKm ?? selectorCursor?.pkInitKm,
        pkInitMeter: selectorData?.pkInitMeter ?? selectorCursor?.pkInitMeter,
        pkEndKm: selectorData?.pkEndKm ?? selectorCursor?.pkEndKm,
        pkEndMeter: selectorData?.pkEndMeter ?? selectorCursor?.pkEndMeter,
        track: selectorData?.track ?? selectorCursor?.track,
        activityId: selectorData?.activityId ?? selectorCursor?.activityId,
        externalCompanyId:
          selectorData?.externalCompanyId ?? selectorCursor?.externalCompanyId,
      };
      dispatch(setCursor(tableId as string, dataCursor));
      setSelectorData((prevData) => ({
        ...prevData,
        concessionId: option.value,
      }));
    },
    [
      selectorCursor,
      selectorData?.indexType,
      selectorData?.indexExpedition,
      selectorData?.subCapitolId,
      selectorData?.capitolId,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData?.classification,
      selectorData?.predefinedOrderId,
      selectorData?.states,
      selectorData?.userIds,
      selectorData?.pkInitKm,
      selectorData?.pkEndKm,
    ]
  );

  const onChangeSelectedStates = useCallback(
    (values: string[]) => {
      // Canonicalize: sort stable so Apollo cache partitioning by `keyArgs: [..., 'states']`
      // produces the same key for the same selection regardless of click order.
      const sorted = [...values].sort();
      const nextStates = sorted.length > 0 ? sorted : undefined;
      const dataCursor = {
        take: selectorCursor.take,
        cursor: selectorCursor?.cursor,
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        subCapitolId:
          selectorData?.subCapitolId ?? selectorCursor?.subCapitolId,
        capitolId: selectorData?.capitolId ?? selectorCursor?.capitolId,
        concessionId: selectorData.concessionId,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        predefinedOrderId:
          selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
        states: nextStates,
        userIds: selectorData?.userIds ?? selectorCursor?.userIds,
        operatorIds: selectorData?.operatorIds ?? selectorCursor?.operatorIds,
        containerTypes:
          selectorData?.containerTypes ?? selectorCursor?.containerTypes,
        teamIds: selectorData?.teamIds ?? selectorCursor?.teamIds,
        includeInactive:
          selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        pkInitKm: selectorData?.pkInitKm ?? selectorCursor?.pkInitKm,
        pkInitMeter: selectorData?.pkInitMeter ?? selectorCursor?.pkInitMeter,
        pkEndKm: selectorData?.pkEndKm ?? selectorCursor?.pkEndKm,
        pkEndMeter: selectorData?.pkEndMeter ?? selectorCursor?.pkEndMeter,
        track: selectorData?.track ?? selectorCursor?.track,
        activityId: selectorData?.activityId ?? selectorCursor?.activityId,
        externalCompanyId:
          selectorData?.externalCompanyId ?? selectorCursor?.externalCompanyId,
      };
      dispatch(setCursor(tableId as string, dataCursor));
      setSelectorData((prevData) => ({
        ...prevData,
        states: nextStates,
      }));
    },
    [
      selectorCursor,
      selectorData?.indexType,
      selectorData?.indexExpedition,
      selectorData?.subCapitolId,
      selectorData.concessionId,
      selectorData?.capitolId,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData?.classification,
      selectorData?.predefinedOrderId,
      selectorData?.userIds,
      selectorData?.pkInitKm,
      selectorData?.pkEndKm,
      dispatch,
      tableId,
    ]
  );

  /**
   * Selector SIMPLE: solo actualiza `selectorData`; el filtro se aplica al
   * pulsar Buscar, igual que el resto de desplegables de esta barra. La
   * opción «Todos» (valor vacío) es la que permite VACIAR el filtro sin
   * tener que limpiar todos los demás con la papelera.
   */
  const onChangeSelectedLaneCutting = (value: option) => {
    setSelectorData((prevData) => ({
      ...prevData,
      // `?? null`: «Todos» tiene que ser DISTINTO de «sin tocar», o el
      // `??` de handleSearch caería al valor viejo del cursor y no limpiaría.
      laneCutting: laneCutSelectionToBoolean(value?.value) ?? null,
    }));
  };

  const onChangeSelectedHasMaterials = (value: option) => {
    setSelectorData((prevData) => ({
      ...prevData,
      // `?? null` por lo mismo que en corte de carril: «Todos» tiene que ser
      // DISTINTO de «sin tocar», o el `??` de handleSearch caería al valor
      // viejo del cursor y el filtro no se limpiaría nunca.
      hasMaterials: hasMaterialsSelectionToBoolean(value?.value) ?? null,
    }));
  };

  const onChangeSelectedMaterialIds = useCallback(
    (values: string[]) => {
      // Canonicalizado igual que el resto de multi-select: el `keyArgs` de
      // Apollo particiona por este valor, así que el mismo conjunto tiene que
      // dar la misma clave sea cual sea el orden de los clics.
      // `values` son los `value` de las opciones AGRUPADAS por nombre, que
      // pueden llevar varios ids dentro; el filtro viaja con ids planos.
      const sorted = expandMaterialFilterValues(values).sort();
      const nextMaterialIds = sorted.length > 0 ? sorted : undefined;
      // 🔴 El dispatch NO es opcional: este control es CONTROLADO
      // (`selectedValues={selectorCursor?.materialIds}`), así que lee del
      // cursor de Redux y no de `selectorData`. Sin escribir el cursor, la
      // marca se perdía en el render siguiente y la casilla salía vacía.
      // El reducer de `SET_CURSOR` hace MERGE, así que basta con el campo
      // que cambia; los demás filtros se conservan.
      dispatch(setCursor(tableId as string, { materialIds: nextMaterialIds }));
      setSelectorData((prevData) => ({
        ...prevData,
        materialIds: nextMaterialIds,
      }));
    },
    [dispatch, tableId]
  );

  const onChangeSelectedUserIds = useCallback(
    (values: string[]) => {
      // Canonicalize: sort stable so Apollo cache partitioning by `keyArgs: [..., 'userIds']`
      // produces the same key for the same selection regardless of click order.
      const sorted = [...values].sort();
      const nextUserIds = sorted.length > 0 ? sorted : undefined;
      const dataCursor = {
        take: selectorCursor.take,
        cursor: selectorCursor?.cursor,
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        subCapitolId:
          selectorData?.subCapitolId ?? selectorCursor?.subCapitolId,
        capitolId: selectorData?.capitolId ?? selectorCursor?.capitolId,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        predefinedOrderId:
          selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
        states: selectorData?.states ?? selectorCursor?.states,
        userIds: nextUserIds,
        operatorIds: selectorData?.operatorIds ?? selectorCursor?.operatorIds,
        containerTypes:
          selectorData?.containerTypes ?? selectorCursor?.containerTypes,
        teamIds: selectorData?.teamIds ?? selectorCursor?.teamIds,
        includeInactive:
          selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        pkInitKm: selectorData?.pkInitKm ?? selectorCursor?.pkInitKm,
        pkInitMeter: selectorData?.pkInitMeter ?? selectorCursor?.pkInitMeter,
        pkEndKm: selectorData?.pkEndKm ?? selectorCursor?.pkEndKm,
        pkEndMeter: selectorData?.pkEndMeter ?? selectorCursor?.pkEndMeter,
        track: selectorData?.track ?? selectorCursor?.track,
        activityId: selectorData?.activityId ?? selectorCursor?.activityId,
        externalCompanyId:
          selectorData?.externalCompanyId ?? selectorCursor?.externalCompanyId,
      };
      dispatch(setCursor(tableId as string, dataCursor));
      setSelectorData((prevData) => ({
        ...prevData,
        userIds: nextUserIds,
      }));
    },
    [
      selectorCursor,
      selectorData?.indexType,
      selectorData?.indexExpedition,
      selectorData?.subCapitolId,
      selectorData?.concessionId,
      selectorData?.capitolId,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData?.classification,
      selectorData?.predefinedOrderId,
      selectorData?.states,
      selectorData?.pkInitKm,
      selectorData?.pkEndKm,
      dispatch,
      tableId,
    ]
  );

  const onChangeSelectedOperatorIds = useCallback(
    (values: string[]) => {
      // Canonicalize: sort stable so Apollo cache partitioning by `keyArgs: [..., 'operatorIds']`
      // produces the same key for the same selection regardless of click order.
      const sorted = [...values].sort();
      const nextOperatorIds = sorted.length > 0 ? sorted : undefined;
      const dataCursor = {
        take: selectorCursor.take,
        cursor: selectorCursor?.cursor,
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        subCapitolId:
          selectorData?.subCapitolId ?? selectorCursor?.subCapitolId,
        capitolId: selectorData?.capitolId ?? selectorCursor?.capitolId,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        predefinedOrderId:
          selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
        states: selectorData?.states ?? selectorCursor?.states,
        userIds: selectorData?.userIds ?? selectorCursor?.userIds,
        operatorIds: nextOperatorIds,
        containerTypes:
          selectorData?.containerTypes ?? selectorCursor?.containerTypes,
        includeInactive:
          selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        pkInitKm: selectorData?.pkInitKm ?? selectorCursor?.pkInitKm,
        pkInitMeter: selectorData?.pkInitMeter ?? selectorCursor?.pkInitMeter,
        pkEndKm: selectorData?.pkEndKm ?? selectorCursor?.pkEndKm,
        pkEndMeter: selectorData?.pkEndMeter ?? selectorCursor?.pkEndMeter,
        track: selectorData?.track ?? selectorCursor?.track,
        activityId: selectorData?.activityId ?? selectorCursor?.activityId,
        externalCompanyId:
          selectorData?.externalCompanyId ?? selectorCursor?.externalCompanyId,
      };
      dispatch(setCursor(tableId as string, dataCursor));
      setSelectorData((prevData) => ({
        ...prevData,
        operatorIds: nextOperatorIds,
      }));
    },
    [
      selectorCursor,
      selectorData?.indexType,
      selectorData?.indexExpedition,
      selectorData?.subCapitolId,
      selectorData?.concessionId,
      selectorData?.capitolId,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData?.classification,
      selectorData?.predefinedOrderId,
      selectorData?.states,
      selectorData?.userIds,
      selectorData?.containerTypes,
      selectorData?.pkInitKm,
      selectorData?.pkEndKm,
      dispatch,
      tableId,
    ]
  );

  // Fase 4 nav Trabajo/Revisión (2026-07-12) — filtro "Origen" (containerType:
  // generic|campaign|surveillanceRoute). Mismo patrón que
  // `onChangeSelectedOperatorIds` — canonicaliza con sort estable. Solo se
  // monta en las tablas GLOBALES (ver gating en el render).
  const onChangeSelectedContainerTypes = useCallback(
    (values: string[]) => {
      const sorted = [...values].sort();
      const nextContainerTypes = sorted.length > 0 ? sorted : undefined;
      const dataCursor = {
        take: selectorCursor.take,
        cursor: selectorCursor?.cursor,
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        subCapitolId:
          selectorData?.subCapitolId ?? selectorCursor?.subCapitolId,
        capitolId: selectorData?.capitolId ?? selectorCursor?.capitolId,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        predefinedOrderId:
          selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
        orderBy: 'desc',
        states: selectorData?.states ?? selectorCursor?.states,
        userIds: selectorData?.userIds ?? selectorCursor?.userIds,
        operatorIds: selectorData?.operatorIds ?? selectorCursor?.operatorIds,
        containerTypes: nextContainerTypes,
        includeInactive:
          selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        pkInitKm: selectorData?.pkInitKm ?? selectorCursor?.pkInitKm,
        pkInitMeter: selectorData?.pkInitMeter ?? selectorCursor?.pkInitMeter,
        pkEndKm: selectorData?.pkEndKm ?? selectorCursor?.pkEndKm,
        pkEndMeter: selectorData?.pkEndMeter ?? selectorCursor?.pkEndMeter,
        track: selectorData?.track ?? selectorCursor?.track,
        activityId: selectorData?.activityId ?? selectorCursor?.activityId,
        externalCompanyId:
          selectorData?.externalCompanyId ?? selectorCursor?.externalCompanyId,
      };
      dispatch(setCursor(tableId as string, dataCursor));
      setSelectorData((prevData) => ({
        ...prevData,
        containerTypes: nextContainerTypes,
      }));
    },
    [
      selectorCursor,
      selectorData?.indexType,
      selectorData?.indexExpedition,
      selectorData?.subCapitolId,
      selectorData?.concessionId,
      selectorData?.capitolId,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData?.classification,
      selectorData?.predefinedOrderId,
      selectorData?.states,
      selectorData?.userIds,
      selectorData?.operatorIds,
      selectorData?.pkInitKm,
      selectorData?.pkEndKm,
      dispatch,
      tableId,
    ]
  );

  const onChangeSelectedTeamIds = useCallback(
    (values: string[]) => {
      // Canonicalize: sort stable so Apollo cache partitioning by `keyArgs: [..., 'teamIds']`
      // produces the same key for the same selection regardless of click order.
      const sorted = [...values].sort();
      const nextTeamIds = sorted.length > 0 ? sorted : undefined;
      const dataCursor = {
        take: selectorCursor.take,
        cursor: selectorCursor?.cursor,
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        subCapitolId:
          selectorData?.subCapitolId ?? selectorCursor?.subCapitolId,
        capitolId: selectorData?.capitolId ?? selectorCursor?.capitolId,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        predefinedOrderId:
          selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
        orderBy: 'desc',
        states: selectorData?.states ?? selectorCursor?.states,
        userIds: selectorData?.userIds ?? selectorCursor?.userIds,
        operatorIds: selectorData?.operatorIds ?? selectorCursor?.operatorIds,
        teamIds: nextTeamIds,
        includeInactive:
          selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        pkInitKm: selectorData?.pkInitKm ?? selectorCursor?.pkInitKm,
        pkInitMeter: selectorData?.pkInitMeter ?? selectorCursor?.pkInitMeter,
        pkEndKm: selectorData?.pkEndKm ?? selectorCursor?.pkEndKm,
        pkEndMeter: selectorData?.pkEndMeter ?? selectorCursor?.pkEndMeter,
        track: selectorData?.track ?? selectorCursor?.track,
        activityId: selectorData?.activityId ?? selectorCursor?.activityId,
        externalCompanyId:
          selectorData?.externalCompanyId ?? selectorCursor?.externalCompanyId,
      };
      dispatch(setCursor(tableId as string, dataCursor));
      setSelectorData((prevData) => ({
        ...prevData,
        teamIds: nextTeamIds,
      }));
    },
    [
      selectorCursor,
      selectorData?.indexType,
      selectorData?.indexExpedition,
      selectorData?.subCapitolId,
      selectorData?.concessionId,
      selectorData?.capitolId,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData?.classification,
      selectorData?.predefinedOrderId,
      selectorData?.states,
      selectorData?.userIds,
      selectorData?.operatorIds,
      selectorData?.pkInitKm,
      selectorData?.pkEndKm,
      dispatch,
      tableId,
    ]
  );

  // «Solo marcadas» (spec 2026-10-22 § 8.3). Solo guarda en el cursor —el
  // reducer hace merge, así que basta con su clave— y la búsqueda la lanza
  // Buscar. Desmarcado es `undefined`, nunca `false`: así la caché de Apollo no
  // se parte en dos claves para «sin filtro». `selectorData` no lleva esta
  // clave (arranca con un objeto propio, no con el cursor), así que el selector
  // de tamaño de página la toma del reflejo del cursor, que ya está al día.
  const onChangeOnlyFlagged = useCallback(
    (next: boolean) => {
      dispatch(
        setCursor(tableId as string, { onlyFlagged: next ? true : undefined })
      );
    },
    [dispatch, tableId]
  );

  // Plan Task 5: toggle "Incluir inactivos en Creador y Operarios".
  // Solo expone usuarios desactivados en los dropdowns; NO filtra resultados
  // del listado. Persiste en el cursor (Redux) y dispara onReFetchData via
  // useEffect aguas abajo (mismo patrón que otros filtros multi-select).
  const onChangeIncludeInactive = useCallback(
    (next: boolean) => {
      const dataCursor = {
        take: selectorCursor.take,
        cursor: '',
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        capitolId: selectorData?.capitolId ?? selectorCursor?.capitolId,
        subCapitolId:
          selectorData?.subCapitolId ?? selectorCursor?.subCapitolId,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        predefinedOrderId:
          selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
        states: selectorData?.states ?? selectorCursor?.states,
        userIds: selectorData?.userIds ?? selectorCursor?.userIds,
        operatorIds: selectorData?.operatorIds ?? selectorCursor?.operatorIds,
        containerTypes:
          selectorData?.containerTypes ?? selectorCursor?.containerTypes,
        teamIds: selectorData?.teamIds ?? selectorCursor?.teamIds,
        includeInactive: next,
        pkInitKm: selectorData?.pkInitKm ?? selectorCursor?.pkInitKm,
        pkInitMeter: selectorData?.pkInitMeter ?? selectorCursor?.pkInitMeter,
        pkEndKm: selectorData?.pkEndKm ?? selectorCursor?.pkEndKm,
        pkEndMeter: selectorData?.pkEndMeter ?? selectorCursor?.pkEndMeter,
        track: selectorData?.track ?? selectorCursor?.track,
        activityId: selectorData?.activityId ?? selectorCursor?.activityId,
        externalCompanyId:
          selectorData?.externalCompanyId ?? selectorCursor?.externalCompanyId,
      };
      dispatch(setCursor(tableId as string, dataCursor));
      setSelectorData((prevData) => ({
        ...prevData,
        includeInactive: next,
      }));
    },
    [selectorCursor, selectorData, dispatch, tableId]
  );

  // T64: Handler for external company filter
  const onChangeSelectedExternalCompany = useCallback(
    (option: option) => {
      const dataCursor = {
        take: selectorCursor.take,
        cursor: '',
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        subCapitolId:
          selectorData?.subCapitolId ?? selectorCursor?.subCapitolId,
        capitolId: selectorData?.capitolId ?? selectorCursor?.capitolId,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        predefinedOrderId:
          selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
        states: selectorData?.states ?? selectorCursor?.states,
        userIds: selectorData?.userIds ?? selectorCursor?.userIds,
        operatorIds: selectorData?.operatorIds ?? selectorCursor?.operatorIds,
        containerTypes:
          selectorData?.containerTypes ?? selectorCursor?.containerTypes,
        teamIds: selectorData?.teamIds ?? selectorCursor?.teamIds,
        includeInactive:
          selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        pkInitKm: selectorData?.pkInitKm ?? selectorCursor?.pkInitKm,
        pkInitMeter: selectorData?.pkInitMeter ?? selectorCursor?.pkInitMeter,
        pkEndKm: selectorData?.pkEndKm ?? selectorCursor?.pkEndKm,
        pkEndMeter: selectorData?.pkEndMeter ?? selectorCursor?.pkEndMeter,
        track: selectorData?.track ?? selectorCursor?.track,
        activityId: selectorData?.activityId ?? selectorCursor?.activityId,
        externalCompanyId: option.value,
      };
      dispatch(setCursor(tableId as string, dataCursor));
      setSelectorData((prevData) => ({
        ...prevData,
        externalCompanyId: option.value,
      }));
    },
    [
      selectorCursor,
      selectorData?.indexType,
      selectorData?.indexExpedition,
      selectorData?.subCapitolId,
      selectorData?.concessionId,
      selectorData?.capitolId,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData?.classification,
      selectorData?.predefinedOrderId,
      selectorData?.states,
      selectorData?.userIds,
      selectorData?.pkInitKm,
      selectorData?.pkEndKm,
    ]
  );

  // Handler for predefined order filter
  const onChangeSelectedPredefinedOrder = useCallback(
    (option: option) => {
      const dataCursor = {
        take: selectorCursor.take,
        cursor: '',
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        subCapitolId:
          selectorData?.subCapitolId ?? selectorCursor?.subCapitolId,
        capitolId: selectorData?.capitolId ?? selectorCursor?.capitolId,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        predefinedOrderId: option.value,
        states: selectorData?.states ?? selectorCursor?.states,
        userIds: selectorData?.userIds ?? selectorCursor?.userIds,
        operatorIds: selectorData?.operatorIds ?? selectorCursor?.operatorIds,
        containerTypes:
          selectorData?.containerTypes ?? selectorCursor?.containerTypes,
        teamIds: selectorData?.teamIds ?? selectorCursor?.teamIds,
        includeInactive:
          selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        pkInitKm: selectorData?.pkInitKm ?? selectorCursor?.pkInitKm,
        pkInitMeter: selectorData?.pkInitMeter ?? selectorCursor?.pkInitMeter,
        pkEndKm: selectorData?.pkEndKm ?? selectorCursor?.pkEndKm,
        pkEndMeter: selectorData?.pkEndMeter ?? selectorCursor?.pkEndMeter,
        track: selectorData?.track ?? selectorCursor?.track,
        activityId: selectorData?.activityId ?? selectorCursor?.activityId,
        externalCompanyId:
          selectorData?.externalCompanyId ?? selectorCursor?.externalCompanyId,
      };
      dispatch(setCursor(tableId as string, dataCursor));
      setSelectorData((prevData) => ({
        ...prevData,
        predefinedOrderId: option.value,
      }));
    },
    [
      selectorCursor,
      selectorData?.indexType,
      selectorData?.indexExpedition,
      selectorData?.subCapitolId,
      selectorData?.concessionId,
      selectorData?.capitolId,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData?.classification,
      selectorData?.states,
      selectorData?.userIds,
      selectorData?.pkInitKm,
      selectorData?.pkEndKm,
    ]
  );

  const onClearValue = useCallback(() => {
    console.log(
      '[ValidationsTable][onClearValue] BEFORE — selectorCursor:',
      selectorCursor,
      '| selectorData:',
      selectorData,
      '| globalCursor:',
      globalCursor
    );
    inputListCapitolRef.current?.clearSelected();
    inputListSubcapitolRef.current?.clearSelected();
    inputDateRef.current?.clearSelected();
    inputExpedientRef.current?.clearSelected();
    inputIndexRef.current?.clearSelected();
    inputIndexRef.current?.clearSelected();
    inputPKInitRef.current?.clearSelected();
    inputPKEndRef.current?.clearSelected();
    inputStateRef.current?.clearSelected();
    inputLaneCutRef.current?.clearSelected();
    inputMaterialsRef.current?.clearSelected();
    inputHasMaterialsRef.current?.clearSelected();
    inputUserRef.current?.clearSelected();
    inputOperatorRef.current?.clearSelected();
    inputContainerTypeRef.current?.clearSelected();
    inputTeamRef.current?.clearSelected();
    inputListClassificationRef.current?.clearSelected();
    inputListTrackRef.current?.clearSelected();
    inputListActivityRef.current?.clearSelected();
    inputExternalCompanyRef.current?.clearSelected();
    inputListPredefinedRef.current?.clearSelected();
    setColumnFilters([]);

    // Check if there's only one concession
    const hasOnlyOneConcession =
      concessionOptions && concessionOptions.length === 1;
    const onlyConcessionId = hasOnlyOneConcession
      ? concessionOptions[0].value
      : undefined;

    // If there's only one concession, keep it selected
    // Otherwise, clear it
    if (!hasOnlyOneConcession) {
      inputListConRef.current?.clearSelected();
    }

    const dataCursor = {
      take: takePagination,
      cursor: '',
      indexType: undefined,
      indexExpedition: undefined,
      subCapitolId: undefined,
      capitolId: undefined,
      concessionId: hasOnlyOneConcession ? onlyConcessionId : undefined,
      fromDate: undefined,
      toDate: undefined,
      classification: undefined,
      track: undefined,
      activityId: undefined,
      predefinedOrderId: undefined,
      sortField: undefined,
      orderBy: 'desc',
      state: undefined,
      states: undefined,
      userIds: undefined,
      operatorIds: undefined,
      containerTypes: undefined,
      teamIds: undefined,
      includeInactive: undefined,
      pkInitKm: undefined,
      pkInitMeter: undefined,
      pkEndKm: undefined,
      pkEndMeter: undefined,
      description: undefined,
      externalCompanyId: undefined,
      laneCutting: undefined,
      materialIds: undefined,
      hasMaterials: undefined,
      // Explícito: el reducer hace merge y omitirlo conservaría el filtro.
      onlyFlagged: undefined,
    };
    dispatch(setCursor(tableId as string, dataCursor));
    setSelectorCursor((prevData) => ({
      ...prevData,
      indexType: undefined,
      indexExpedition: undefined,
      subCapitolId: undefined,
      capitolId: undefined,
      concessionId: hasOnlyOneConcession ? onlyConcessionId : undefined,
      fromDate: undefined,
      toDate: undefined,
      classification: undefined,
      track: undefined,
      activityId: undefined,
      predefinedOrderId: undefined,
      sortField: undefined,
      orderBy: 'desc',
      state: undefined,
      states: undefined,
      userIds: undefined,
      operatorIds: undefined,
      containerTypes: undefined,
      teamIds: undefined,
      includeInactive: undefined,
      pkInitKm: undefined,
      pkInitMeter: undefined,
      pkEndKm: undefined,
      pkEndMeter: undefined,
      description: undefined,
      externalCompanyId: undefined,
      onlyFlagged: undefined,
    }));
    setSelectorData((prevData) => ({
      ...prevData,
      indexType: undefined,
      indexExpedition: undefined,
      subCapitolId: undefined,
      capitolId: undefined,
      concessionId: hasOnlyOneConcession ? onlyConcessionId : undefined,
      fromDate: undefined,
      toDate: undefined,
      classification: undefined,
      track: undefined,
      activityId: undefined,
      predefinedOrderId: undefined,
      state: undefined,
      states: undefined,
      userIds: undefined,
      operatorIds: undefined,
      containerTypes: undefined,
      teamIds: undefined,
      includeInactive: undefined,
      pkInitKm: undefined,
      pkInitMeter: undefined,
      pkEndKm: undefined,
      pkEndMeter: undefined,
      description: undefined,
      externalCompanyId: undefined,
    }));
    setSubCapitolOptions(initialSubCapitolsOptions);
    setCurrentServerPage(1);
    onReFetchData?.();
    onClearSelection?.(); // Bug #2: al cambiar/limpiar el filtro se reinicia la selección (no en paginación)
    // Tarea #11 fix: `takePagination` debe estar en deps para evitar stale closure.
  }, [
    concessionOptions,
    onChangeInputExpedient,
    onChangeInputIndex,
    takePagination,
  ]);

  const handleClickRow = (row: Row<dataTable>) => {
    if (isCheckboxMode) {
      // Checkbox mode: toggle via TanStack (calls onRowSelectionChange → parent)
      row.toggleSelected(!row.getIsSelected());
    } else {
      // Radio mode: call parent's setRowState directly
      const val = row.getIsSelected();
      if (!val) {
        radioSetRowState?.(row.original.actionID || '');
      } else {
        radioSetRowState?.('');
      }
    }
  };

  // Sync rowSelection → setRowState for RADIO mode only
  // In checkbox mode, selection is controlled by parent via usePersistentSelection hook.
  useEffect(() => {
    if (isCheckboxMode) return; // Checkbox mode is fully controlled by parent
    const selectedIds = Object.keys(internalRowSelection).filter(
      (key) => (internalRowSelection as Record<string, boolean>)[key]
    );
    if (selectedIds.length > 0) {
      const selectedRow = table.getRow(selectedIds[0]);
      radioSetRowState?.(selectedRow?.original?.actionID || '');
    } else {
      radioSetRowState?.('');
    }
  }, [internalRowSelection, radioSetRowState, table, isCheckboxMode]);

  const fetchOrderID = useCallback(
    async (id: string, take = takePagination) => {
      const dataSelector = {
        take,
        cursor: id,
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        sortField: selectorCursor?.sortField,
        orderBy: selectorCursor?.orderBy ?? 'desc',
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        capitolId: selectorData?.capitolId ?? selectorCursor?.capitolId,
        subCapitolId:
          selectorData?.subCapitolId ?? selectorCursor?.subCapitolId,
        predefinedOrderId:
          selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
        states: selectorData?.states ?? selectorCursor?.states,
        userIds: selectorData?.userIds ?? selectorCursor?.userIds,
        operatorIds: selectorData?.operatorIds ?? selectorCursor?.operatorIds,
        containerTypes:
          selectorData?.containerTypes ?? selectorCursor?.containerTypes,
        teamIds: selectorData?.teamIds ?? selectorCursor?.teamIds,
        includeInactive:
          selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        pkInitKm: selectorData?.pkInitKm ?? selectorCursor?.pkInitKm,
        pkInitMeter: selectorData?.pkInitMeter ?? selectorCursor?.pkInitMeter,
        pkEndKm: selectorData?.pkEndKm ?? selectorCursor?.pkEndKm,
        pkEndMeter: selectorData?.pkEndMeter ?? selectorCursor?.pkEndMeter,
        track: selectorData?.track ?? selectorCursor?.track,
        activityId: selectorData?.activityId ?? selectorCursor?.activityId,
        externalCompanyId:
          selectorData?.externalCompanyId ?? selectorCursor?.externalCompanyId,
      };

      if (take > 0) {
        const lastAction = dataAll[dataAll.length - 1].actionID;
        if (lastAction) {
          dispatch(setCursor(tableId as string, dataSelector));
        }
      } else {
        const firstAction = dataAll[0].actionID;

        if (firstAction) {
          dispatch(setCursor(tableId as string, dataSelector));
        }
      }

      return dataSelector;
    },
    [
      selectorCursor,
      selectorData?.indexType,
      selectorData?.indexExpedition,
      selectorData?.concessionId,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData?.classification,
      selectorData?.capitolId,
      selectorData?.subCapitolId,
      selectorData?.predefinedOrderId,
      selectorData?.states,
      selectorData?.userIds,
      selectorData?.pkInitKm,
      selectorData?.pkEndKm,
      dataAll,
      takePagination,
    ]
  );

  const nextPagination = async () => {
    // Clamp defensivo contra bug off-by-one (backend/cache hasNextPage true en última real).
    const pageCount = Math.max(
      1,
      Math.ceil((data?.totalCount ?? 0) / takePagination)
    );
    if (
      dataAll.length >= 1 &&
      data.pageInfo.hasNextPage &&
      currentServerPage < pageCount
    ) {
      setCurrentServerPage((prev) => Math.min(prev + 1, pageCount));
      await fetchOrderID(dataAll[dataAll.length - 1].actionID as string);
      onReFetchPagination();
    }
  };

  const backPagination = async () => {
    if (dataAll.length >= 1 && data.pageInfo.hasPreviousPage) {
      setCurrentServerPage((prev) => Math.max(1, prev - 1));
      await fetchOrderID(dataAll[0].actionID as string, -takePagination);
      onReFetchPagination();
    }
  };

  // Columnas que ofrece el desplegable «Columnas»: todas menos la de selección
  // (la primera) y las que no se pueden ocultar (la bandera, § 8.2). Sin
  // bandera queda igual que el `.slice(1)` de siempre.
  const hideableColumns = table
    .getAllColumns()
    .slice(1)
    .filter((column) => column.getCanHide());

  const hiddenColumns: number = Object.values(
    columnVisibility as object
  ).reduce((count, currentValue) => (!currentValue ? count + 1 : count), 0);

  const handleSearch = async () => {
    const dataCursor = {
      take: takePagination,
      cursor: '',
      indexType: selectorData?.indexType ?? selectorCursor?.indexType,
      indexExpedition:
        selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
      subCapitolId: selectorData?.subCapitolId ?? selectorCursor?.subCapitolId,
      capitolId: selectorData?.capitolId ?? selectorCursor?.capitolId,
      concessionId: selectorData.concessionId,
      fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
      toDate: selectorData?.toDate ?? selectorCursor?.toDate,
      classification:
        selectorData?.classification ?? selectorCursor?.classification,
      predefinedOrderId:
        selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
      states: selectorData?.states ?? selectorCursor?.states,
      userIds: selectorData?.userIds ?? selectorCursor?.userIds,
      operatorIds: selectorData?.operatorIds ?? selectorCursor?.operatorIds,
      containerTypes:
        selectorData?.containerTypes ?? selectorCursor?.containerTypes,
      teamIds: selectorData?.teamIds ?? selectorCursor?.teamIds,
      materialIds: selectorData?.materialIds ?? selectorCursor?.materialIds,
      // `??` y NUNCA `||`: `false` («sin materiales») es un filtro válido.
      hasMaterials:
        selectorData?.hasMaterials === null
          ? undefined
          : (selectorData?.hasMaterials ?? selectorCursor?.hasMaterials),
      includeInactive:
        selectorData?.includeInactive ?? selectorCursor?.includeInactive,
      pkInitKm: selectorData?.pkInitKm ?? selectorCursor?.pkInitKm,
      pkInitMeter: selectorData?.pkInitMeter ?? selectorCursor?.pkInitMeter,
      pkEndKm: selectorData?.pkEndKm ?? selectorCursor?.pkEndKm,
      pkEndMeter: selectorData?.pkEndMeter ?? selectorCursor?.pkEndMeter,
      description:
        (selectorData?.description ?? selectorCursor?.description) || undefined,
      // `??` y NUNCA `||`: `false` es un filtro válido («No») y `||` lo
      // convertiría en «sin filtrar».
      laneCutting:
        selectorData?.laneCutting === null
          ? undefined
          : (selectorData?.laneCutting ?? selectorCursor?.laneCutting),
      track: selectorData?.track ?? selectorCursor?.track,
      activityId: selectorData?.activityId ?? selectorCursor?.activityId,
      externalCompanyId:
        selectorData?.externalCompanyId ?? selectorCursor?.externalCompanyId,
    };
    dispatch(setCursor(tableId as string, dataCursor));
    setCurrentServerPage(1);

    onReFetchData?.();
    onClearSelection?.(); // Bug #2: al buscar con filtro nuevo se reinicia la selección
  };

  const filterContainerRef = useRef<HTMLDivElement>(null);
  useFilterKeyboardShortcuts(
    filterContainerRef,
    handleSearch,
    onClearValue,
    isLoadingData as boolean
  );

  return (
    <ContainerStyled>
      {isLoadingData && (
        <ContainerLoader>
          <Loader />
        </ContainerLoader>
      )}
      <FiltersWithLoadingStyled isLoading={isLoadingData}>
        <SubContainerHead>
          <Title component={'h2'} variant={'semiBold'} size={'M'}>
            {titleText}
          </Title>
          {isLanscapeTablet && (
            <ShowFiltersButton isBlack={showFilters} onClick={handleClick}>
              <ArrowBottomStyled isRotate={showFilters} />
            </ShowFiltersButton>
          )}
        </SubContainerHead>
        {showFilters && (
          <SubContainerFiltersStyled ref={filterContainerRef}>
            <InputExpedient
              label={t('headers.expedient')}
              disabled={isLoadingData}
              expedient={expedientDefault}
              onChangeInputExpedient={onChangeInputExpedient}
              ref={inputExpedientRef}
            />

            <InputIndex
              label={t('headers.index')}
              disabled={isLoadingData}
              index={indexDefault}
              onChangeInputIndex={onChangeInputIndex}
              ref={inputIndexRef}
            />
            <InputPK
              label={t('tables:headers.pkInit')}
              disabled={isLoadingData}
              onChangeInputPK={onChangePKInit}
              ref={inputPKInitRef}
            />
            <InputPK
              label={t('tables:headers.pkEnd')}
              disabled={isLoadingData}
              onChangeInputPK={onChangePKEnd}
              ref={inputPKEndRef}
            />
            {/* Filtro por descripción: texto libre sobre Order.description.
                Busca por subcadena, sin distinguir acentos ni mayúsculas, y
                exige todas las palabras en cualquier orden (el backend filtra
                contra la copia normalizada `descriptionSearch`, indexada). */}
            <InputText
              label={t('tables:headers.description')}
              typeInput="normal"
              inputSize="S"
              placeholder={t('common:placeholders.writeHere')}
              disabled={isLoadingData}
              value={
                selectorData?.description ?? selectorCursor?.description ?? ''
              }
              onChange={(e) =>
                setSelectorData((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
            />
            <InputCalendarStyled
              ref={inputDateRef}
              label={t('tables:filters.dateRangeFilter')}
              name={'data'}
              onChangeInput={onChangeInput}
              dateRange={
                selectorCursor?.fromDate && selectorCursor?.toDate
                  ? {
                      from: parseISO(selectorCursor?.fromDate),
                      to: parseISO(selectorCursor?.toDate),
                    }
                  : undefined
              }
              disabled={isLoadingData}
            />
            {concessionOptions && concessionOptions.length > 1 && (
              <DropDownInput
                inputSize="XS"
                ref={inputListConRef}
                labelText={t('tables:headers.concession')}
                placeholder={t('common:placeholders.select')}
                typeDropDown={'Secondary'}
                defaultValue={selectorCursor?.concessionId ?? ''}
                options={concessionOptions as Options}
                onChangeSelected={onChangeSelectedConcession as onChangeValue}
                isDisabled={isLoadingData}
              />
            )}
            {showCapitolFilter && (
              <SearchableDropdownStyledBig
                inputSize="L"
                ref={inputListCapitolRef}
                labelText={'Capitol'}
                placeholder={t('common:placeholders.select')}
                typeDropDown={'Default'}
                defaultValue={selectorCursor?.capitolId ?? ''}
                options={capitolOptions as Options}
                onChangeSelected={onChangeSelectedCapitolGroup as onChangeValue}
                isDisabled={isLoadingData}
              />
            )}

            {showSubCapitolFilter && (
              <SearchableDropdownStyledBig
                inputSize="L"
                ref={inputListSubcapitolRef}
                labelText={'Subcapitol'}
                placeholder={t('common:placeholders.select')}
                typeDropDown={'Default'}
                defaultValue={selectorCursor?.subCapitolId ?? ''}
                options={subCapitolOptions as Options}
                onChangeSelected={
                  onChangeSelectedSubcapitolGroup as onChangeValue
                }
                isDisabled={isLoadingData}
              />
            )}

            <SearchableDropdownStyledBig
              inputSize="L"
              ref={inputListClassificationRef}
              labelText={t('tables:headers.classification')}
              placeholder={t('common:placeholders.select')}
              typeDropDown={'Default'}
              defaultValue={selectorCursor?.classification ?? ''}
              options={classificationOptions as Options}
              onChangeSelected={onChangeSelectedClassification as onChangeValue}
              isDisabled={isLoadingData}
            />

            <SearchableDropdownStyledBig
              inputSize="L"
              ref={inputListTrackRef}
              labelText={t('forms:workOrder.roadType')}
              placeholder={t('common:placeholders.select')}
              typeDropDown={'Default'}
              defaultValue={selectorCursor?.track ?? ''}
              options={trackOptions as Options}
              onChangeSelected={onChangeSelectedTrack as onChangeValue}
              isDisabled={isLoadingData}
            />

            {activityOptions && activityOptions.length > 0 && (
              <SearchableDropdownStyledBig
                inputSize="L"
                ref={inputListActivityRef}
                labelText={t('forms:workOrder.action')}
                placeholder={t('common:placeholders.select')}
                typeDropDown={'Default'}
                defaultValue={selectorCursor?.activityId ?? ''}
                options={activityOptions as Options}
                onChangeSelected={onChangeSelectedActivity as onChangeValue}
                isDisabled={isLoadingData}
              />
            )}

            {showPredefinedFilter &&
              predefinedOrderOptions &&
              predefinedOrderOptions.length > 0 && (
                <SearchableDropdownStyledBig
                  inputSize="L"
                  ref={inputListPredefinedRef}
                  labelText={t('forms:workOrder.predefined')}
                  placeholder={t('common:placeholders.select')}
                  typeDropDown={'Default'}
                  defaultValue={selectorCursor?.predefinedOrderId ?? ''}
                  options={predefinedOrderOptions as Options}
                  onChangeSelected={
                    onChangeSelectedPredefinedOrder as onChangeValue
                  }
                  isDisabled={isLoadingData}
                />
              )}

            {externalCompanyOptions && externalCompanyOptions.length > 0 && (
              <SearchableDropdownStyledBig
                inputSize="L"
                ref={inputExternalCompanyRef}
                labelText={t('common:tables.headers.externalCompanies')}
                placeholder={t('common:placeholders.select')}
                typeDropDown={'Default'}
                defaultValue={selectorCursor?.externalCompanyId ?? ''}
                options={externalCompanyOptions as Options}
                onChangeSelected={
                  onChangeSelectedExternalCompany as onChangeValue
                }
                isDisabled={isLoadingData}
              />
            )}

            <MultiSelectDropdownStyledBig
              inputSize="S"
              ref={inputStateRef}
              labelText={t('tables:headers.status')}
              placeholder={t('common:placeholders.select')}
              typeDropDown={'Secondary'}
              selectedValues={selectorCursor?.states ?? []}
              options={
                roleActive
                  ? getStateOptionsByRoleAndEntityType(
                      t,
                      roleActive as Role,
                      TABLE_ENTITY_TYPES[tableId as string],
                      listingFiltersByRole(tableId)
                    )
                  : []
              }
              onChangeSelected={onChangeSelectedStates}
              isDisabled={isLoadingData}
            />
            <DropDownInputStyledBig
              inputSize="S"
              ref={inputLaneCutRef}
              labelText={t('common:tables.headers.laneCut')}
              placeholder={t('common:placeholders.select')}
              typeDropDown={'Default'}
              defaultValue={booleanToLaneCutSelection(
                selectorCursor?.laneCutting
              )}
              options={getLaneCutFilterOptions(t) as Options}
              onChangeSelected={onChangeSelectedLaneCutting as onChangeValue}
              isDisabled={isLoadingData}
            />
            {/* Ancho L y buscable como Creador y Operarios: son 144
                materiales, una lista sin buscador no es manejable. */}
            <MultiSelectDropdownStyledBig
              inputSize="L"
              ref={inputMaterialsRef}
              labelText={t('workOrders:materials')}
              placeholder={t('common:placeholders.select')}
              typeDropDown={'Default'}
              searchable
              selectedValues={selectedMaterialValues}
              options={materialFilterOptions}
              onChangeSelected={onChangeSelectedMaterialIds}
              isDisabled={isLoadingData}
            />
            <DropDownInputStyledBig
              inputSize="S"
              ref={inputHasMaterialsRef}
              labelText={t('common:tables.headers.hasMaterials')}
              placeholder={t('common:placeholders.select')}
              typeDropDown={'Default'}
              defaultValue={booleanToHasMaterialsSelection(
                selectorCursor?.hasMaterials
              )}
              options={getHasMaterialsFilterOptions(t) as Options}
              onChangeSelected={onChangeSelectedHasMaterials as onChangeValue}
              isDisabled={isLoadingData}
            />

            {userOptions && userOptions.length > 0 && (
              <MultiSelectDropdownStyledBig
                inputSize="L"
                ref={inputUserRef}
                labelText={t('common:labels.creator')}
                placeholder={t('common:placeholders.select')}
                typeDropDown={'Default'}
                searchable
                selectedValues={selectorCursor?.userIds ?? []}
                options={userOptions}
                onChangeSelected={onChangeSelectedUserIds}
                isDisabled={isLoadingData}
              />
            )}

            {!isRvScopedTable &&
              operatorOptions &&
              operatorOptions.length > 0 && (
                <MultiSelectDropdownStyledBig
                  inputSize="L"
                  ref={inputOperatorRef}
                  labelText={t('common:tabs.operators')}
                  placeholder={t('common:placeholders.select')}
                  typeDropDown={'Default'}
                  searchable
                  selectedValues={selectorCursor?.operatorIds ?? []}
                  options={operatorOptions}
                  onChangeSelected={onChangeSelectedOperatorIds}
                  isDisabled={isLoadingData}
                />
              )}

            {/* Fase 4 nav Trabajo/Revisión (2026-07-12) — filtro "Origen".
                Oculto en pestañas contextuales (campaña/RV). */}
            {!isContainerScopedTable && (
              <MultiSelectDropdownStyledBig
                inputSize="S"
                ref={inputContainerTypeRef}
                labelText={t('common:tables.headers.origin')}
                placeholder={t('common:placeholders.select')}
                typeDropDown={'Secondary'}
                selectedValues={selectorCursor?.containerTypes ?? []}
                options={getContainerTypeOptions(t)}
                onChangeSelected={onChangeSelectedContainerTypes}
                isDisabled={isLoadingData}
              />
            )}

            {teamOptions && teamOptions.length > 0 && (
              <MultiSelectDropdownStyledBig
                inputSize="L"
                ref={inputTeamRef}
                labelText={t('forms:workOrder.team')}
                placeholder={t('common:placeholders.select')}
                typeDropDown={'Default'}
                selectedValues={selectorCursor?.teamIds ?? []}
                options={teamOptions}
                onChangeSelected={onChangeSelectedTeamIds}
                isDisabled={isLoadingData}
              />
            )}

            <ContainerButtonStyled>
              <ButtonIconBackground
                variant={'delete'}
                onClick={onClearValue}
                disabled={isLoadingData}
                title={`${t('tables:filters.clearFilters')} (Shift+Enter)`}
              />
              <DropDownVisibilityColumn
                columnNames={Object.fromEntries(
                  hideableColumns.map((c) => [
                    c.id,
                    translatedColumnNames[
                      c.id as keyof typeof translatedColumnNames
                    ]?.label || c.id,
                  ])
                )}
                columns={hideableColumns}
                isDisabled={isLoadingData}
              />
              <ButtonIconBackground
                variant={'restart'}
                onClick={() => {
                  setCurrentServerPage(1);
                  onReFetchData?.();
                }}
                disabled={isLoadingData}
              />
              <ButtonIconBackground
                variant={'search'}
                onClick={handleSearch}
                disabled={isLoadingData}
                title={`${t('buttons:search')} (Enter)`}
              />
            </ContainerButtonStyled>
            <IncludeInactiveToggle
              checked={selectorCursor?.includeInactive ?? false}
              onChange={onChangeIncludeInactive}
              label={t(
                isRvScopedTable
                  ? 'common:filters.includeInactiveInCreator'
                  : 'common:filters.includeInactiveInCreatorAndOperators'
              )}
              isDisabled={isLoadingData}
            />
            {showOnlyFlaggedFilter && (
              <IncludeInactiveToggle
                checked={Boolean(selectorCursor?.onlyFlagged)}
                onChange={onChangeOnlyFlagged}
                label={flagScopeTexts.onlyFlagged}
                isDisabled={isLoadingData}
              />
            )}
          </SubContainerFiltersStyled>
        )}
      </FiltersWithLoadingStyled>
      <TableContainerStyled isLoading={isLoadingData}>
        <Table {...props}>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <Th
                    key={header.id}
                    {...exportIgnoreProps(header.column.id)}
                    aria-sort={
                      header.column.id === activeSortColumnId
                        ? sortDirection === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : undefined
                    }
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </Th>
                ))}
              </tr>
            ))}
          </thead>
          <Tbody>
            {table.getRowModel().rows.map((row) => {
              return (
                <DoubleTapRow
                  key={row.id}
                  StyledComponent={TrStyled}
                  componentProps={{
                    checked: row.getIsSelected(),
                    indeterminate: !table.getIsSomeRowsSelected(),
                  }}
                  isDisabled={isLoadingData as boolean}
                  onSingleTap={() => {
                    handleClickRow(row);
                    row.getToggleSelectedHandler()(row);
                  }}
                  onDoubleTap={async () => {
                    // La pantalla sabe adónde se vuelve: si lo dice, manda ella.
                    if (onRowOpen) {
                      if (row.original?.actionID) onRowOpen(row.original.actionID);
                      return;
                    }
                    // BUG #13b FIX: Añadir mismo cleanup que otros puntos de entrada
                    if (row.original?.actionID) {
                      try {
                        // 1. Obtener datos de la acción
                        const {
                          data: { action },
                        } = await client.query({
                          query: GET_ACTION_BY_ID,
                          variables: { id: row.original.actionID },
                          fetchPolicy: 'network-only',
                        });

                        // 2. Obtener datos de la orden
                        const {
                          data: { order },
                        } = await client.query({
                          query: GET_ORDER_BY_ID,
                          variables: { id: action.orderIDs[0] },
                          fetchPolicy: 'network-only',
                        });

                        // 3. CLEANUP: Limpiar sessionStorage de validación anterior
                        Object.values({
                          ...formActionNaming,
                          ...formActionValidationNaming,
                          ...externalCompaniesNaming,
                        }).forEach((key) => {
                          if (key !== formActionNaming.NEW_ACTION_DATA) {
                            sessionStorage.removeItem(key);
                          }
                        });

                        // 4. ESTABLECER: isActionEdited: 'false' para nueva validación
                        sessionStorage.setItem(
                          formActionNaming.NEW_VALIDATION_ACTION_DATA,
                          compressSessionStorage(
                            JSON.stringify({
                              id: row.original.actionID,
                              orderId: order.id,
                              roid: order.roid,
                              isActionEdited: 'false',
                              returnTo: '/work-orders/actions-validation',
                            })
                          )
                        );

                        // 5. FLUJO VALIDACIÓN: Desbloquear TODAS las tabs
                        dispatch(actionTabsUnlockAll());

                        // 6. NAVEGAR
                        navigate(
                          `/work-orders/actions-validation/summary/${row.original.actionID}`
                        );
                      } catch (error) {
                        console.error(
                          'Error in onDoubleTap navigation:',
                          error
                        );
                      }
                    }
                  }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TdStyled
                      column={cell.column.id as columnID}
                      hiddenColumns={hiddenColumns}
                      key={cell.id}
                      {...exportIgnoreProps(cell.column.id)}
                    >
                      <>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </>
                    </TdStyled>
                  ))}
                </DoubleTapRow>
              );
            })}
          </Tbody>
        </Table>
      </TableContainerStyled>
      {isCheckboxMode && onSelectAll && onClearSelection && (
        <SelectionBanner
          selectedCount={propsSelectedCount}
          totalCount={data?.totalCount || 0}
          pageCount={dataAll.length}
          allPageSelected={
            dataAll.length > 0 &&
            dataAll.every(
              (row: any) => (rowSelection as Record<string, boolean>)[row.id]
            )
          }
          selectAllMode={selectAllMode}
          onSelectAll={onSelectAll}
          onClearSelection={onClearSelection}
        />
      )}
      <PaginationButtons
        pageCount={Math.max(
          1,
          Math.ceil((data?.totalCount ?? 0) / takePagination)
        )}
        currentPage={currentServerPage}
        totalCount={data?.totalCount ?? 0}
        nextPage={nextPagination}
        previousPage={backPagination}
        isDisabled={isLoadingData}
        // Tarea #11: gates aplicados en `useServerPageSize`.
        {...paginationProps}
      />
    </ContainerStyled>
  );
};
